/**
 * SampleLoginView — the Sample Login workspace (stepper steps 2–4), faithful to
 * the approved prototype and the legacy ALPACA / BTLIMS screens.
 *
 * A real TAB CONTROL with its own body per tab; the record-level Save actions
 * belong to the whole registration and are supplied by the host page:
 *
 *   Sample Login  — the wide sample grid (30+ configurable columns, the first
 *                   two FROZEN) + the Test Assignment panel for the current row.
 *   Test View     — one row per sample × test (editable IsGroup + Status).
 *   Containers    — bottle allocation, By Sample (master-detail) or All in One.
 *   Parameters    — one row per sample × test × analyte; FIELD-method results
 *                   are keyed here, lab methods stay read-only (Result Entry).
 *
 * Legacy behaviours reproduced:
 *   · one current record (the ▶ row) drives Test Assignment — BTLIMS parity;
 *   · right-click an editable cell → Copy to All Cells / Copy to Selected Rows
 *     (the shared <CellContextMenu>);
 *   · Copy Samples clones the marked sample (fields + tests + bottles) N times;
 *   · Copy Test copies one sample's test set onto other samples;
 *   · tests sharing a container + preservative share ONE bottle, and Qty is the
 *     number of DISTINCT bottles (never Qty 1 with 5 bottles);
 *   · Find Column jumps to and flashes any of the 30+ columns.
 *
 * TODO(AI-29131): every mutation is local state.  The sample / test / bottle
 * endpoints must accept these shapes (see srData.ts) and re-derive the bottle
 * grouping + hold times server-side (§1 — business logic lives in the API).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './sampleRegistration.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { ComboSelect } from '../../../components/ComboSelect';
import { CellContextMenu, canBulkCopy } from '../../../components/gridEdit';
import { ExportMenu, type ExportMenuHandle } from '../../../components/AppToolbar';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { api } from '../../../api/client';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { ColumnFinder } from '../../../components/ColumnFinder/ColumnFinder';
import { UsDateTimeInput } from '../../../components/UsDateTimeInput';
import { CopySamplesDialog, CopyTestDialog, AddSiteDialog } from './SampleLoginDialogs';
import { StationPickerDialog } from './StationPickerDialog';
import { CopyBottlesDialog } from './CopyBottlesDialog';
import { inertProps } from './inertProps';
import {
  SAMPLE_STATUS_OPTS, SL_COLS,
  autoBottles, assignBottleLetters, cloneTest, copyTest, mkTest, renumber,
  syncQty, qtyIsManual, markQtyManual, clearQtyManual, testDefaults, toDateInput, isFieldTest, guideSets, guideFirstDefault, GuidKeyMap, guidePreservFor,
  type BottleLookups, type SrBottleDetail, type SrParamData, type SrReceivedDate, type SrSample, type SrTest,
} from './srData';
import { useConfirm } from '../../../components/confirm';
import { markUserEdit } from '../../../hooks/useUnsavedGuard';

/**
 * Tell the shared unsaved-changes guard the USER just changed sample data
 * (standing rule 15).
 *
 * These grids keep their rows in the PARENT's state and publish them with a
 * programmatic `setSamples`, so there is no native input event for the automatic
 * tracker to watch. Worse, a grid cell editor is REMOVED from the DOM the moment the
 * edit commits — or when Esc cancels it — and the guard deliberately forgets a control
 * that has left the page ("form closed → nothing to lose"). So an edit typed into the
 * Samples / Tests / Containers grids stopped counting as unsaved, and navigating away
 * discarded it with no prompt (user report 2026-10-14).
 *
 * Marking a LONG-LIVED element instead keeps the edit counted until it is saved or
 * discarded. The page root is used because each grid lives in its own sub-component
 * (and the Containers popup renders in a dialog over it), so there is no single ref
 * they all share. The Test Assignment popup has done exactly this since 2026-10-10.
 *
 * Call this ONLY from a user action — the hydration effects that call `setSamples`
 * directly (restoring saved parameters, auto-grouping bottles, filling a stale
 * ReceivedDate) must NOT mark the form dirty, or opening a job would prompt on exit.
 */
function markSamplesEdited(): void {
  markUserEdit(document.querySelector('.content'));
}

// ── Test Parameter cache (DB-driven, replaces hardcoded PARAMS) ─────────

/** Cache key = "testName|method|comp" → parameter name list. */
const _paramCache = new Map<string, string[]>();
/** Parallel cache: same key → { parameterName → testparameterOid }. */
const _paramOidCache = new Map<string, Record<string, string>>();
/** Test-code default Units (Testparameter.DefaultUnits) per test+component → parameter name. */
const _paramUnitCache = new Map<string, Record<string, string>>();
let _paramVer = 0;

function paramCacheKey(t: { name: string; method: string; comp: string }): string {
  return `${t.name}|${t.method}|${t.comp || 'Default'}`;
}

/** GoLive delete guards, asked of the server BEFORE a saved test is removed: a test that
 *  has a result, sits in SDMS / a QC batch / sample preparation / a sub-out order (or is
 *  signed off) cannot be deleted. Returns the message to show, or null when every test may
 *  go. Tests that were never saved (no testMethodOid) and unsaved jobs are always
 *  removable. A failed request returns null — Save enforces the same rules authoritatively. */
export async function testDeleteBlockMessage(
  jobOid: string | null | undefined,
  items: { sampleId: string; test: { testMethodOid?: string; comp?: string } }[],
): Promise<string | null> {
  if (!jobOid) return null;
  const body = items.flatMap(i => {
    const no = Number.parseInt(i.sampleId.slice(i.sampleId.lastIndexOf('-') + 1), 10);
    return i.test.testMethodOid && no > 0
      ? [{ sampleNo: no, testMethodOid: i.test.testMethodOid, component: i.test.comp || 'Default' }]
      : [];
  });
  if (body.length === 0) return null;
  try {
    const res = await api.post(`/api/v1/sample-registration/${encodeURIComponent(jobOid)}/check-test-delete`, { items: body });
    const blocked = ((res.data as { data?: { blocked?: { sampleNo: number; reason: string }[] } }).data?.blocked) ?? [];
    if (blocked.length === 0) return null;
    const prefix = items[0]!.sampleId.slice(0, items[0]!.sampleId.lastIndexOf('-'));
    return [...new Set(blocked.map(b => `${prefix}-${String(b.sampleNo).padStart(3, '0')}: ${b.reason}`))].join('\n');
  } catch {
    return null;
  }
}

type GroupChild = { name: string; method: string; comp: string; isFieldTest?: boolean; oid?: string };

/** The 'testgroup' lookup lists every child test once PER COMPONENT (so the popup can
 *  show them all). Adding a GROUP to a sample must add each child test ONCE — at its
 *  Default component, as the Test Template path already does — otherwise a child with
 *  three components (e.g. Anions_IC: Watershed / Nitrate / Default) lands three times.
 *  A child with no Default component keeps its first row. */
export function defaultComponentChildren(children: GroupChild[]): GroupChild[] {
  const byTest = new Map<string, GroupChild>();
  for (const ch of children) {
    const k = `${ch.name}|${ch.method}`;
    const cur = byTest.get(k);
    if (!cur || ((cur.comp || 'Default') !== 'Default' && (ch.comp || 'Default') === 'Default')) byTest.set(k, ch);
  }
  return [...byTest.values()];
}

export async function fetchTestParams(t: { name: string; method: string; comp: string }): Promise<string[]> {
  const k = paramCacheKey(t);
  const cached = _paramCache.get(k);
  if (cached) return cached;
  // paramLabel decides All Param vs Customized by comparing the selected analyte
  // count to the test method's DEFAULT-component total (legacy SRInfo.Totparam), so
  // whenever a named sub-component is loaded make sure the Default list is cached
  // first — awaited here so a component's cache is never warm without its Default.
  // The Default list is requested IN PARALLEL with this component's own request (each is
  // a round trip) and awaited before this component's cache is written, so the ordering
  // guarantee above still holds without paying two sequential round trips.
  const defaultReady = (t.comp || 'Default') !== 'Default'
    ? fetchTestParams({ name: t.name, method: t.method, comp: 'Default' })
    : null;
  try {
    const res = await api.get('/api/v1/sample-registration/test-parameters', {
      params: { testName: t.name, methodNumber: t.method, component: t.comp || 'Default' },
    });
    if (defaultReady) await defaultReady;
    const rows: { parameterName: string; testparameterOid: string; defaultUnits?: string | null }[] = res.data?.data ?? [];
    const names: string[] = rows.map(r => r.parameterName);
    const oidMap: Record<string, string> = {};
    for (const r of rows) oidMap[r.parameterName] = r.testparameterOid;
    const unitMap: Record<string, string> = {};
    for (const r of rows) if (r.defaultUnits) unitMap[r.parameterName] = r.defaultUnits;
    _paramUnitCache.set(k, unitMap);
    if (names.length === 0) names.push(t.name);
    _paramCache.set(k, names);
    _paramOidCache.set(k, oidMap);
    _paramVer++;
    return names;
  } catch {
    const fallback = [t.name];
    _paramCache.set(k, fallback);
    _paramVer++;
    return fallback;
  }
}

export function cachedParams(t: { name: string; method: string; comp: string }): string[] | undefined {
  return _paramCache.get(paramCacheKey(t));
}

/** Test-code default Units for a parameter (empty when the test code sets none). */
export function cachedParamUnit(t: { name: string; method: string; comp: string }, paramName: string): string | undefined {
  return _paramUnitCache.get(paramCacheKey(t))?.[paramName];
}

/** Resolve parameterName → Testparameter.Oid for a given test. */
export function cachedParamOid(t: { name: string; method: string; comp: string }, paramName: string): string | undefined {
  return _paramOidCache.get(paramCacheKey(t))?.[paramName];
}

function paramLabel(t: SrTest): string {
  const list = cachedParams(t);
  if (!list) return '…';
  // Analytes actually included on this test (params null = every analyte of its
  // component).
  const included = t.params ? Object.values(t.params).filter(Boolean).length : list.length;
  // Legacy parity (SampleRegistrationViewController: selectedCount == SRInfo.Totparam
  // ? "AllParam" : "Customised", where Totparam is the DEFAULT component's parameter
  // count). So a named sub-component — e.g. Metals_ICP_MS "Lead & Copper" (2 analytes)
  // or "UCMR4" (1) vs the Default's 18 — reads as Customized, not All Param, because
  // it is a subset of the test's full analyte set. fetchTestParams keeps the Default
  // list cached alongside every sub-component, so this is populated when list is.
  const defaultList = (t.comp || 'Default') === 'Default'
    ? list
    : cachedParams({ name: t.name, method: t.method, comp: 'Default' });
  if (!defaultList) return '…';
  return included === defaultList.length ? 'All Param' : 'Customized';
}

// ── Bottle / Container reference lookups (module-level cache) ────────────────

const EMPTY_BL: BottleLookups = {
  containers: [], preservatives: [], storages: [], storageConditions: [], testGuide: [],
};

/** Cache: testMethodOids key (or '' for unfiltered) → result. */
const _blCache = new Map<string, BottleLookups>();
const _blPending = new Map<string, Promise<BottleLookups>>();

function parseBl(d: Record<string, unknown> | undefined): BottleLookups {
  return {
    containers:       (d?.containers       as BottleLookups['containers'])       ?? [],
    preservatives:    (d?.preservatives    as BottleLookups['preservatives'])    ?? [],
    storages:         (d?.storages         as BottleLookups['storages'])         ?? [],
    storageConditions:(d?.storageConditions as BottleLookups['storageConditions'])?? [],
    testGuide:        (d?.testGuide        as BottleLookups['testGuide'])        ?? [],
  };
}

async function fetchBottleLookups(testMethodOids?: string): Promise<BottleLookups> {
  const key = testMethodOids ?? '';
  const cached = _blCache.get(key);
  if (cached) return cached;
  const pending = _blPending.get(key);
  if (pending) return pending;

  const params = key ? { testMethodOids: key } : undefined;
  const p = api.get('/api/v1/sample-registration/bottle-lookups', { params })
    .then(res => {
      const bl = parseBl(res.data?.data);
      _blCache.set(key, bl);
      return bl;
    })
    .catch(() => { _blCache.set(key, EMPTY_BL); return EMPTY_BL; })
    .finally(() => { _blPending.delete(key); });
  _blPending.set(key, p);
  return p;
}

function useBottleLookups(testMethodOids?: string): BottleLookups {
  const key = testMethodOids ?? '';
  const [bl, setBl] = useState<BottleLookups>(() => _blCache.get(key) ?? EMPTY_BL);
  useEffect(() => {
    const cached = _blCache.get(key);
    if (cached) { setBl(cached); return; }
    fetchBottleLookups(testMethodOids).then(setBl);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return bl;
}

/** Is this value a GUID string (vs a display name / legacy text)? */
const isGuidValue = (v?: string | null): boolean =>
  !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

// ── Bottle-sharing groups per Sample Matrix (Assign Bottles) ─────────────────
// Each group is a list of TestMethod Oids that share ONE physical bottle (legacy
// dbo.BottleSharing). Cached per matrix Oid.
const _shareCache = new Map<string, string[][]>();
async function fetchBottleSharing(matrixOid: string): Promise<string[][]> {
  const cached = _shareCache.get(matrixOid);
  if (cached) return cached;
  try {
    const res = await api.get('/api/v1/sample-registration/bottle-sharing', { params: { matrix: matrixOid } });
    const groups = ((res.data?.data as string[][]) ?? []).filter(g => Array.isArray(g));
    _shareCache.set(matrixOid, groups);
    return groups;
  } catch {
    _shareCache.set(matrixOid, []);
    return [];
  }
}

/** Resolve a sample's VisualMatrix Oid from its stored value (OID in the dynamic
 *  config path; falls back to the legacy key, then the job's primary matrix). */
function sampleMatrixOid(s: SrSample | undefined, primaryMatrixOid?: string | null): string | null {
  if (!s) return primaryMatrixOid ?? null;
  const sm = s.v.SampleMatrix;
  if (sm && isGuidValue(sm)) return sm;
  const vm = s.v.VisualMatrix;
  if (vm && isGuidValue(vm)) return vm;
  return primaryMatrixOid ?? null;
}

// ── Sample Login config (from AI_SampleRegistration_GetSampleLoginConfig) ────

interface LoginFieldCfg {
  fieldId: string;
  caption: string;
  width: number;
  sortOrder: number;
  freeze: boolean;
  fieldType: string;
  lookupSource: string | null;
  isMandatory: boolean;
  isReadOnly: boolean;
}

interface LoginLookupOpt {
  oid: string;
  name: string;
  visualMatrixGuids: string;
  address: string;
  samplingAddress: string;
  keyMap: string;
  samplePointId: string;
  samplePointType: string;
  pwsId: string;
  waterTypeName: string;
  systemTypeOid: string;
  rejectionCriteriaOid: string;
  parentSampleID: string;
  parentSampleDate: string;
}

interface SampleLoginCfg {
  fields: LoginFieldCfg[];
  visualMatrices: LoginLookupOpt[];
  sampleTypes: LoginLookupOpt[];
  collectors: LoginLookupOpt[];
  sampleStatuses: LoginLookupOpt[];
  sampleSites: LoginLookupOpt[];
  systemTypes: LoginLookupOpt[];
  qcCategories: LoginLookupOpt[];
  departments: LoginLookupOpt[];
  rejectionCriteria: LoginLookupOpt[];
  repeatLocations: LoginLookupOpt[];
  // Sample-Login lookup masters (G5 parity 2026-09-30) — option lists keyed by
  // FieldID (Bare, Kind, Code, …). RS12 of the config SP; resolved by getLookupOpts.
  extraLookups?: Record<string, LoginLookupOpt[]>;
}

// Maps XAF FieldID names (from dbo.SampleMatrixSetupFields) to the SrSample.v[]
// keys used throughout this module. When a FieldID matches an SL_COLS key exactly
// it is omitted (the identity mapping is the default: vKey = fieldId ?? fieldId).
const FIELD_ID_TO_V_KEY: Record<string, string> = {
  ClientSampleID:     'SampleName',
  StationLocation:    'SiteName',
  CollectDate:        'DateCollected',
  CollectTimeDisplay: 'TimeCollected',
  VisualMatrix:       'SampleMatrix',   // OID stored under SL_COLS key
  ChlorineFree:       'FreeColorine(mg/L)',
  ChlorineTotal:      'TotalChlorine(mg/L)',
  LoginBy:            'EnteredBy',
  LoginDateTime:      'EnteredDate',
};

// Lookup list key → config property name. The SP returns lookupSource as the
// camelCase key matching SampleLoginCfg properties — this helper resolves the
// options array at runtime so the column builder is fully SP-driven.
const getLookupOpts = (cfg: SampleLoginCfg, src: string | null): LoginLookupOpt[] => {
  if (!src) return [];
  // Direct config property (camelCase keys: visualMatrices, sampleTypes, …).
  const direct = (cfg as unknown as Record<string, LoginLookupOpt[] | undefined>)[src];
  if (direct) return direct;
  // Fall back to the Sample-Login lookup-master lists keyed by FieldID
  // (Bare, Kind, …) — RS12 of the config SP (G5 parity 2026-09-30).
  return cfg.extraLookups?.[src] ?? [];
};

export type SlTab = 'login' | 'testview' | 'containers' | 'params';

export interface SampleLoginViewProps {
  jobId: string;
  /** The server-assigned GUID of the job, used to fetch the matrix-driven
   *  Sample Login column config.  Null for a new, never-saved job. */
  jobOid?: string | null;
  /** OID of the primary (first) selected matrix — used to load the column config
   *  for new jobs that do not have a jobOid yet.  Null when no matrix selected. */
  primaryMatrixOid?: string | null;
  /** OID of the client chosen on the registration header — used to filter the
   *  SampleSites dropdown to that client's sites in New Mode. */
  clientOid?: string | null;
  samples: SrSample[];
  /** Replace the sample set (the host owns the state; this view mutates a copy). */
  setSamples: (next: SrSample[]) => void;
  tab: SlTab;
  setTab: (t: SlTab) => void;
  /** The current record index (the ▶ row). */
  active: number;
  setActive: (i: number) => void;
  /** Record-level actions rendered on the tab strip (Save / Save and Close / Cancel). */
  actions: React.ReactNode;
  /** Return to the full Registration detail view (step 1). */
  onBackToRegistration: () => void;
  onWarn: (message: string) => void;
  onInfo: (message: string) => void;
  onSuccess: (message: string) => void;
  confirmAction: (title: string, message: string, run: () => void, danger?: boolean) => void;
  /** Trigger a full server reload of the job (samples + tests + bottles).
   *  Used after server-side copy / reanalysis on persisted jobs. */
  onServerReload?: () => void;
  /** Open the "New Collector" quick-add from the Sample Login toolbar. */
  onNewCollector: () => void;
  /** A collector just created by the quick-add popup — appended to the grid's
   *  Collector dropdown (config.collectors) and selected into the active row, then
   *  cleared via onCollectorConsumed (user report 2026-10-05). */
  newCollector?: { oid: string; name: string } | null;
  onCollectorConsumed?: () => void;
  /** Live test catalog rows from the API: [name, method, isGroup, component]. */
  testRows?: string[][];
  /** Group test name → child tests (expanded from GroupTestMethod). */
  groupChildren?: Record<string, { name: string; method: string; comp: string; isFieldTest?: boolean; oid?: string }[]>;
  /** OID → display name for VisualMatrix (built from the matrix lookup). */
  matrixMap?: Record<string, string>;
  /** OID → display name for Collector (built from the collector lookup) — used to
   *  show the field-test AnalyzedBy as the collector's NAME, not the raw Oid. */
  collectorMap?: Record<string, string>;
  /** Collector names chosen in the registration header — when non-empty the grid's
   *  Collector dropdown is limited to these (legacy CollectorDataSource). */
  headerCollectorNames?: string[];
  /** Unit names from dbo.Unit for the Parameters tab Units dropdown. */
  unitOptions?: string[];
  /** Employee display names (global roster) for the Parameters tab AnalyzedBy
   *  dropdown — field-test results are stamped with an analyst here (ALPACA
   *  SampleParameter_ListView AnalyzedBy lookup). */
  employeeOptions?: string[];
  /** Received date rows from the SC_ReceivedDates tab — used to populate the
   *  ReceivedDate dropdown in the Sample Login grid.  Auto-fills the cell when
   *  there is exactly one row; shows a dropdown when there are multiple. */
  receivedDates?: SrReceivedDate[];
  /** Record duplicate-parameter justifications captured in the Test Assignment
   *  popup (legacy ValidateDuplicateAnalysis → Reason). Lifted to the detail page
   *  so persist() can audit each reason at page Save. */
  recordDuplicateJustifications?: (
    sampleId: string,
    items: { paramName: string; testNames: string[]; reason: string }[],
  ) => void;
  /** Parameter names already justified as duplicates for this job (ALPACA parity):
   *  the Test Assignment duplicate alert skips these so it fires once per param. */
  justifiedParams?: Set<string>;
  /** MAC read-only mode: the tab bodies are inert (tabs can still be switched to view). */
  readOnly?: boolean;
}

export function SampleLoginView(props: SampleLoginViewProps) {
  const { samples, setSamples, tab, setTab, actions, onBackToRegistration } = props;

  // ── Restore a customized analyte selection after a (re)load ──────────────
  // The load mapper only knows the analyte NAMES that were saved (savedParamNames);
  // the "which are unchecked" half needs the test's full list, so expand it here once
  // that list is cached: a strict subset → params map (label "Customized", popup shows
  // N of M), the full set → null (All Param).
  const samplesRef = useRef(samples);
  samplesRef.current = samples;
  const restoreKey = useMemo(() => {
    const ks: string[] = [];
    for (const s of samples) for (const t of s.tests) if (t.savedParamNames) ks.push(`${s.id}|${t.key}`);
    return ks.join(';');
  }, [samples]);
  useEffect(() => {
    if (!restoreKey) return;
    let dead = false;
    const pending = new Map<string, SrTest>();
    for (const s of samplesRef.current) for (const t of s.tests) {
      if (t.savedParamNames) pending.set(paramCacheKey(t), t);
    }
    void Promise.all([...pending.values()].map(t => fetchTestParams(t))).then(() => {
      if (dead) return;
      let changed = false;
      const next = samplesRef.current.map(s => ({
        ...s,
        tests: s.tests.map(t => {
          if (!t.savedParamNames) return t;
          const list = cachedParams(t);
          if (!list) return t;
          const saved = new Set(t.savedParamNames);
          const c = copyTest(t);
          delete c.savedParamNames;
          changed = true;
          const all = list.every(n => saved.has(n));
          // Only restore when the saved names actually belong to this list.
          if (!all && list.some(n => saved.has(n))) c.params = Object.fromEntries(list.map(n => [n, saved.has(n)]));
          return c;
        }),
      }));
      if (changed) setSamples(next);
    });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreKey]);

  // ── Auto-load the TOP Container / Preservative from the test's TestGuide ──
  // When a test's TestGuide (Method Defaults → Test Guide) lists container /
  // preservative rows, pre-fill the test with the FIRST (top) one so a newly
  // added test never lands with a blank Container/Preservative (owner decision
  // 2026-09-30 — ALPACA `addnewtestbottles` only auto-fills when there is exactly
  // one distinct value, leaving Calcium-style multi-container tests blank). The
  // user can still change it from the combo, which offers every allowed value; a
  // method with no TestGuide rows keeps its existing default. Applied once per
  // test, across every tab and the bottle picker (all read shared `samples`).
  const guideOids = useMemo(() => {
    const ids = new Set<string>();
    for (const s of samples) for (const t of s.tests) if (t.testMethodOid) ids.add(t.testMethodOid);
    return [...ids].sort().join(',');
  }, [samples]);
  const guideBl = useBottleLookups(guideOids || undefined);
  const guideApplied = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (guideBl.testGuide.length === 0) return;
    const { containers, preservatives } = guideSets(guideBl.testGuide);
    let changed = false;
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    for (const s of draft) {
      for (const t of s.tests) {
        if (!t.testMethodOid || guideApplied.current.has(t.key)) continue;
        // Round-cause B: a test loaded WITH a persisted bottle allocation is
        // authoritative — never overwrite its saved Container / Preservative with
        // the single-default (legacy AssignBottleAllocationToSamples is
        // non-destructive). Mark it applied so it is skipped for good.
        if (t.hasSavedBottle) { guideApplied.current.add(t.key); continue; }
        const gc = guideFirstDefault(containers.get(t.testMethodOid));
        // The preservative PAIRED with the chosen container in the Test Guide row
        // (a guide row is a Container+Preservative pair), else the top preservative.
        const gp = (gc !== undefined ? guidePreservFor(guideBl.testGuide, t.testMethodOid, gc) : undefined)
          ?? guideFirstDefault(preservatives.get(t.testMethodOid));
        if (gc === undefined && gp === undefined) continue; // no guide for this method — keep as-is
        if (gc !== undefined && t.container !== gc) { t.container = gc; changed = true; }
        if (gp !== undefined && t.preserv !== gp) { t.preserv = gp; changed = true; }
        guideApplied.current.add(t.key);
      }
    }
    if (changed) {
      // Round-cause B: only a pure NEW / registration sample (no persisted bottle
      // allocations) gets the auto bottle grouping + Qty recompute. A SAVED sample
      // keeps its authoritative BottleID / Qty — regrouping would collapse a manual
      // multi-bottle setup (A,B,C) back to 'A' and overwrite the persisted Qty, which
      // is the reported bug ("BottleID reverts to A after clicking Containers",
      // "Sample Qty keeps changing"). Legacy Qty is a manual, persistent field.
      for (const s of draft) {
        if (!s.tests.some(t => t.hasSavedBottle)) autoBottles(s);
      }
      setSamples(draft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideBl.testGuide, samples]);

  return (
    <section className="sr-sec">
      <div className="sr-sec-b" style={{ paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="ca-btn" onClick={onBackToRegistration}
            title="Back to the Sample Registration detail view">
            <span style={{ color: 'var(--ai-blue)' }}>‹</span> Sample Registration
          </button>
          <div className="sr-tabbar" style={{ flex: 1, margin: 0 }} role="tablist">
            {([
              ['login', 'Samples'], ['testview', 'Tests'],
              ['containers', 'Containers'], ['params', 'Parameters'],
            ] as [SlTab, string][]).map(([id, label]) => (
              <button key={id} role="tab" aria-selected={tab === id}
                className={`sr-tb${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>
                {label}
              </button>
            ))}
          </div>
          {actions}
        </div>

        <div style={{ paddingTop: 10 }} {...inertProps(!!props.readOnly)}>
          {tab === 'login' && <LoginTab {...props} />}
          {tab === 'testview' && <TestViewTab {...props} />}
          {tab === 'containers' && <ContainersTab {...props} />}
          {tab === 'params' && <ParametersTab {...props} />}
        </div>

        {samples.length === 0 && (
          <p className="sr-note" style={{ marginTop: 8 }}>
            No samples yet — set <b>No Of Samples</b> on the Registration step, then return here.
          </p>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** The static boolean Hold column appended at the far right when the matrix
 *  config does not include its own Hold field.  Defined outside the component
 *  to avoid recreating the column def on every render. */
function staticHoldCol(
  mutate: (fn: (draft: SrSample[]) => void) => void,
  samples: SrSample[],
): AppColumn<SrSample> {
  return {
    field: 'hold', header: 'Hold', width: 54, align: 'center',
    sortable: false, filterable: false,
    render: s => (
      <input type="checkbox" checked={s.hold} aria-label={`Hold ${s.id}`}
        onClick={e => e.stopPropagation()}
        onChange={e => {
          const checked = e.target.checked;
          mutate(draft => { const t = draft.find(x => x.id === s.id); if (t) t.hold = checked; });
        }} />
    ),
  };
  // samples is a dep only so the column def rebuilds when sample set changes (closure)
  void samples;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample Login tab — the wide grid + Test Assignment
// ─────────────────────────────────────────────────────────────────────────────
function LoginTab({
  jobId, jobOid, primaryMatrixOid, clientOid, samples, setSamples, active, setActive, onWarn,
  onSuccess, confirmAction, onServerReload, onNewCollector, newCollector, onCollectorConsumed, headerCollectorNames,
  testRows = [], groupChildren = {},
  receivedDates = [], unitOptions, matrixMap, recordDuplicateJustifications, justifiedParams,
}: SampleLoginViewProps) {
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyCount, setCopyCount] = useState(1);
  const [copyTestOpen, setCopyTestOpen] = useState(false);
  // Test Edit popup (legacy TestEdit dashboard) and the source sample forced onto
  // Copy Tests when launched from inside it.
  const [testEditId, setTestEditId] = useState<string | null>(null);
  const [copyTestFrom, setCopyTestFrom] = useState<string | null>(null);
  const [siteOpen, setSiteOpen] = useState(false);
  const [ctx, setCtx] = useState<{ col: string; label: string; value: string; x: number; y: number } | null>(null);
  // The sample row whose AlternativeStation is being picked (null = closed).
  // Opens the shared StationPickerDialog (G5 "Alternative Station" popup parity).
  const [stationPick, setStationPick] = useState<string | null>(null);
  // Test / Cont per-row popups (ALPACA parity — the "Test" and "Containers" row
  // actions open a focused popup for that sample). Hold the sample id (null =
  // closed); the active row is set to that sample so the reused TestAssignment /
  // ContainersTab render it.
  const [testPopup, setTestPopup] = useState<string | null>(null);
  const [contPopup, setContPopup] = useState<string | null>(null);
  const [contTestKey, setContTestKey] = useState<string | null>(null);  // Test Assignment BottleID → pre-focused test
  const scrollRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<ExportMenuHandle>(null);
  // Test Assignment panel — Test Edit scrolls/flashes it into view so clicking
  // Test Edit on a checked sample gives visible feedback (G5's Test Edit opened
  // a dedicated dashboard; G6 edits that sample's tests in this inline panel).
  const testPanelRef = useRef<HTMLDivElement>(null);

  // ── Reports ▾ (Custom Report Builder — SampleLogIn_ListView_Copy_SampleRegistration) ──
  interface FormReportItem { id: number; label: string; templateName: string; allowMultipleJobId: boolean; }
  const [reportItems, setReportItems] = useState<FormReportItem[]>([]);
  const [reportsOpen, setReportsOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get('/api/v1/sample-registration/login-reports')
      .then(res => {
        const list = ((res.data as { data?: FormReportItem[] })?.data ?? [])
          .filter(r => r?.templateName);
        if (alive) setReportItems(list);
      })
      .catch(() => { if (alive) setReportItems([]); });
    return () => { alive = false; };
  }, []);
  const previewReport = useCallback(async (item: FormReportItem) => {
    setReportsOpen(false);
    if (!jobId) { onWarn('Save the job before previewing a report.'); return; }
    try {
      const checkedSampleIds = sel.size > 0
        ? samples.filter(s => sel.has(s.id)).map(s => s.id).join(',')
        : undefined;
      const res = await api.get('/api/v1/sample-registration/login-report-pdf', {
        params: { templateName: item.templateName, jobId, sampleIds: checkedSampleIds },
        responseType: 'blob',
      });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) {
        onWarn(`${item.label} produced no content.`);
        return;
      }
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err: unknown) {
      // Axios with responseType:'blob' wraps server errors as Blob — read text to surface real message.
      let detail = '';
      if (err && typeof err === 'object' && 'response' in err) {
        const axErr = err as { response?: { data?: unknown; status?: number } };
        if (axErr.response?.data instanceof Blob) {
          try { detail = ': ' + await (axErr.response.data as Blob).text(); } catch { /* ignore */ }
        } else if (axErr.response?.data) {
          detail = ': ' + String(axErr.response.data);
        }
        if (!detail && axErr.response?.status) detail = ` (HTTP ${axErr.response.status})`;
      } else if (err instanceof Error) {
        detail = ': ' + err.message;
      }
      onWarn(`Failed to preview "${item.label}"${detail}`);
    }
  }, [jobId, samples, sel, onWarn]);

  // ── Dynamic Sample Login config (matrix-driven columns + lookup lists) ────
  const [config, setConfig] = useState<SampleLoginCfg | null>(null);

  useEffect(() => {
    let dead = false;
    let url: string | null = null;
    if (jobOid) {
      // Edit mode: fetch config keyed by the saved job so the SP can resolve
      // the primary matrix from Samplecheckin.SampleMatries.
      url = `/api/v1/sample-registration/${jobOid}/sample-login-config`;
    } else if (primaryMatrixOid) {
      // New mode: job not yet saved — load config directly by matrix OID.
      const params = clientOid ? `?clientOid=${clientOid}` : '';
      url = `/api/v1/sample-registration/sample-login-config/by-matrix/${primaryMatrixOid}${params}`;
    }
    if (!url) return;
    api.get(url)
      .then(r => {
        if (!dead) setConfig((r.data as { data: SampleLoginCfg }).data ?? null);
      })
      .catch(() => { /* non-critical — falls back to static SL_COLS */ });
    return () => { dead = true; };
  }, [jobOid, primaryMatrixOid, clientOid]);

  /** Sample ids whose Received Date the user explicitly cleared (N/A). */
  const rdClearedRef = useRef<Set<string>>(new Set());
  // Auto-fill ReceivedDate when exactly one received date exists and the cell is empty.
  useEffect(() => {
    if (receivedDates.length !== 1) return;
    const singleOid = receivedDates[0]!.oid;
    if (!singleOid) return;
    // Bug 13: a Save re-creates the SC_ReceivedDates rows under NEW Oids, so a sample
    // still holding the OLD Oid reads blank (and its dropdown looked disabled). A
    // stale value is treated like an empty one and re-pointed at the single receipt.
    // A cell the user deliberately set to N/A is EMPTY on purpose — never refill it
    // (bug 4, 2026-10-16); only a stale non-empty Oid or a never-touched empty cell is.
    const isStale = (s: SrSample) => {
      const v = s.v['ReceivedDate'];
      return v ? !receivedDates.some(r => r.oid === v) : !rdClearedRef.current.has(s.id);
    };
    const needsFill = samples.some(isStale);
    if (!needsFill) return;
    const draft = samples.map(s => ({
      ...s, v: { ...s.v },
      tests: s.tests.map(copyTest),
    }));
    for (const s of draft) {
      if (isStale(s)) s.v['ReceivedDate'] = singleOid;
    }
    setSamples(draft);
    // Runs when a new set of dates is loaded OR the samples are replaced (a reload
    // after Save); it is a no-op once nothing is stale, so it cannot loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedDates, samples]);

  /** Mutate a copy of the sample set and publish it. Every USER edit in the Samples
   *  grid comes through here, so this is where the unsaved-changes guard is told. */
  const mutate = useCallback((fn: (draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    fn(draft);
    setSamples(draft);
    markSamplesEdited();
  }, [samples, setSamples]);

  /** Cascade a sample's SampleStatus (already stored in s.v.SampleStatus as an Oid) to
   *  every test of that sample and sync SubOut/Hold. Shared by a single-cell edit AND
   *  Copy to All Cells — which used to copy only the cell value, so a Sub Out copied to
   *  all samples changed just the active sample's Test Assignment (user report 2026-10-08). */
  const cascadeSampleStatus = (s: SrSample, storeValue: string) => {
    if (!config) return;
    const opts = getLookupOpts(config, 'sampleStatuses');
    const statusName = opts.find(o => o.oid === storeValue)?.name ?? '';
    const isSub = statusName === 'Sub Out';
    s.v['SubOut'] = isSub ? '1' : '0';
    for (const t of s.tests) { t.status = statusName; t.subOut = isSub; }
    // Task 29935 (ALPACA parity): an "Uncollected" sample has no collection info,
    // so clear its Collector, Date Collected and Time Collected — matching legacy
    // SampleRegistrationViewController.BatchEditEndEditing. The native time picker
    // accepts empty, so '' is the AI equivalent of legacy's '00:00' no-time sentinel.
    if (statusName === 'Uncollected') {
      s.v['DateCollected'] = '';
      s.v['TimeCollected'] = '';
      s.v['Collector'] = '';
    }
    // XAF: clearing status resets both Hold and SubOut on the sample and all tests
    if (!statusName) {
      s.v['Hold'] = '0';
      s.v['SubOut'] = '0';
      s.hold = false;
      for (const t of s.tests) { t.hold = false; t.subOut = false; }
    }
  };

  // Qty is the user's BOTTLE COUNT — it drives the bottle list in the picker
  // ({SampleID}-A…), so it must not be recomputed from the current bottles here
  // (that is `syncQty`, and it belongs only after a MANUAL bottle change).
  const setCell = (rowId: string, col: string, value: string) => {
    mutate(draft => {
      const s = draft.find(x => x.id === rowId);
      if (!s) return;
      // For dropdown columns resolve the selected display name → OID before
      // storing so fkName() (which looks up by oid) works correctly after edit,
      // and the save body receives a valid GUID (not a display name string).
      let storeValue = value;
      if (config) {
        const fCfg = config.fields.find(f => (FIELD_ID_TO_V_KEY[f.fieldId] ?? f.fieldId) === col);
        if (fCfg?.fieldType === 'dropdown' && fCfg.lookupSource) {
          const opts = getLookupOpts(config, fCfg.lookupSource);
          const found = opts.find(o => o.name === value);
          storeValue = found?.oid ?? value;
        }
      }
      s.v[col] = storeValue;
      // Qty is user-owned once typed (legacy: SampleLogIn.Qty != 0) — mark it so
      // adding a test / re-assigning bottles never auto-overwrites it again.
      if (col === 'Qty') markQtyManual(s);
      // Cascade sample status to all tests within the sample and sync SubOut/Hold
      if (col === 'SampleStatus') cascadeSampleStatus(s, storeValue);
    });
  };

  // A collector just created by the "Add Collector" quick-add: add it to the grid's
  // Collector dropdown options (config.collectors — the dropdown reads from the grid
  // config, NOT the header lookups) and select it into the active row, then clear
  // the hand-off. Store the OID directly (the column renders the name via
  // config.collectors); setCell's name→oid resolution can't be used here because the
  // config update is async. (user report 2026-10-05)
  useEffect(() => {
    if (!newCollector || !newCollector.oid) return;
    const { oid, name } = newCollector;
    setConfig(cfg => {
      if (!cfg) return cfg;
      if (cfg.collectors.some(c => c.oid === oid)) return cfg;
      // Only oid + name matter for a collector; the other LoginLookupOpt fields
      // are site-only — default them to '' to satisfy the type.
      const opt: LoginLookupOpt = {
        oid, name,
        visualMatrixGuids: '', address: '', samplingAddress: '', keyMap: '',
        samplePointId: '', samplePointType: '', pwsId: '', waterTypeName: '',
        systemTypeOid: '', rejectionCriteriaOid: '', parentSampleID: '', parentSampleDate: '',
      };
      // INSERT in alphabetical position — ALPACA's collector dropdown is sorted by
      // name, so a new collector appears in its sorted place, NOT jammed at the top.
      // (Prepending put "test-1" above "A Tran…" — user report 2026-10-05.) The
      // existing list is already name-sorted (SP ORDER BY), so this preserves it.
      const next = [...cfg.collectors];
      const at = next.findIndex(c => c.name.localeCompare(opt.name, undefined, { sensitivity: 'base' }) > 0);
      if (at < 0) next.push(opt); else next.splice(at, 0, opt);
      return { ...cfg, collectors: next };
    });
    // ALPACA parity (user request 2026-10-06): a newly-added collector is added to
    // the dropdown ONLY — it is NOT auto-selected into the active row. The user picks
    // it from the Collector dropdown manually. (Was selected into the active row
    // before; the select-into-row mutate was removed.)
    onCollectorConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCollector]);

  /**
   * Fill every site-derived column from the picked SampleSites row — legacy
   * SampleRegistrationViewController's StationLocation callback. Shared by the
   * single-cell edit AND "Copy To All Cell", because in G5 copying a SiteName
   * carries its SamplingAddress / Address / KeyMap / … with it; copying the name
   * alone left the other site columns blank (user report 2026-10-03).
   */
  const applySiteFill = (s: SrSample, found: LoginLookupOpt) => {
    s.v['SiteName']           = found.oid;
    s.v['Address']            = found.address;
    s.v['SamplingAddress']    = found.samplingAddress;
    s.v['KeyMap']             = found.keyMap;
    s.v['SamplePointID']      = found.samplePointId;
    s.v['SamplePointType']    = found.samplePointType;
    s.v['PWSID']              = found.pwsId;
    s.v['WaterType']          = found.waterTypeName;
    // Auto-fill FK OIDs from site attributes
    if (found.systemTypeOid)         s.v['SystemType']        = found.systemTypeOid;
    if (found.rejectionCriteriaOid)  s.v['RejectionCriteria'] = found.rejectionCriteriaOid;
    if (found.parentSampleID)        s.v['ParentSampleID']    = found.parentSampleID;
    if (found.parentSampleDate)      s.v['ParentSampleDate']  = found.parentSampleDate;
  };

  const copyColumn = (col: string, displayValue: string, onlySelected: boolean) => {
    // ReceivedDate: resolve formatted date label → SC_ReceivedDates.Oid
    let storeValue = displayValue;
    let site: LoginLookupOpt | undefined;
    if (col === 'ReceivedDate') {
      const rd = receivedDates.find(r => fmtLocalDateTime(r.date) === displayValue);
      storeValue = rd?.oid ?? displayValue;
    } else if (config) {
      const fCfg = config.fields.find(f => (FIELD_ID_TO_V_KEY[f.fieldId] ?? f.fieldId) === col);
      if (fCfg?.fieldType === 'dropdown' && fCfg.lookupSource) {
        const opts = getLookupOpts(config, fCfg.lookupSource);
        const found = opts.find(o => o.name === displayValue);
        storeValue = found?.oid ?? displayValue;
        if (col === 'SiteName') site = found;
      }
    }
    mutate(draft => {
      for (const s of draft) {
        if (onlySelected && !sel.has(s.id)) continue;
        if (site) { applySiteFill(s, site); continue; }
        s.v[col] = storeValue;
        if (col === 'Qty') markQtyManual(s);
        // Same cascade as a single-cell edit: every copied sample's tests follow its status.
        if (col === 'SampleStatus') cascadeSampleStatus(s, storeValue);
      }
    });
    onSuccess(`Copied "${displayValue}" to ${onlySelected ? sel.size : samples.length} cell(s).`);
  };

  // ── Helper: OID → display name for FK columns ─────────────────────────────
  // Returns empty string (not the raw GUID) when no match is found so cells
  // never show a GUID to the user.
  // Case-INSENSITIVE Oid match: SQL CAST(... AS NVARCHAR) yields UPPERCASE GUIDs
  // (the header lookups) while Guid.ToString() yields lowercase (the config option
  // lists), so a value seeded from one source would never match the other with an
  // exact compare — e.g. the header-defaulted Collector showed blank (user report
  // 2026-10-03). Normalise both ends.
  const fkName = (opts: LoginLookupOpt[], oid: string) => {
    const target = (oid ?? '').toLowerCase();
    return (target ? opts.find(o => (o.oid ?? '').toLowerCase() === target)?.name : '') ?? '';
  };

  // G5 parity for the Freeze flag (user report 2026-10-01: WaterType Freeze=1 did
  // not freeze in G6). Legacy SampleRegistrationViewController sets
  // FixedStyle=GridViewColumnFixedStyle.Left on EACH Freeze=true column (lines
  // 9323-9325 / 11686-11688), so DevExpress pins it to the frozen-left region
  // REGARDLESS of its SortOrder position. An HTML sticky grid can only freeze the
  // leftmost CONTIGUOUS columns, so we render all Freeze=true fields FIRST (stable
  // order), then the rest — the sticky region (stickyCount) then covers exactly
  // those freeze columns, matching G5. SampleID is the base frozen column (rendered
  // separately) and is kept out of the freeze group so it is not double-counted.
  const orderedFields = useMemo(() => {
    if (!config) return [];
    const isFreeze = (f: (typeof config.fields)[number]) => f.freeze && f.fieldId !== 'SampleID';
    return [...config.fields.filter(isFreeze), ...config.fields.filter(f => !isFreeze(f))];
  }, [config]);

  // ── Columns: ▶ record indicator · SampleID (frozen) · the configurable set · Hold
  const cols: AppColumn<SrSample>[] = useMemo(() => {
    const out: AppColumn<SrSample>[] = [
      {
        field: '__rec', header: '', width: 26, align: 'center',
        sortable: false, filterable: false, noExport: true,
        render: s => (samples[active]?.id === s.id
          ? <span style={{ color: 'var(--brand-accent, var(--ai-orange))', fontSize: 11 }}>▶</span>
          : null),
      },
      // ── Test / Cont row-link columns (ALPACA parity) ─────────────────────────
      // Legacy SampleRegistrationViewController renders "Test" and "Containers"
      // as per-row action columns right after the selection checkbox and BEFORE
      // SampleID (line 9290), each a hyperlink opening that sample's test /
      // container view. In AI the equivalent: "Test" focuses the row and jumps to
      // the Test Assignment panel on this tab; "Cont" focuses the row and switches
      // to the Containers tab. Both frozen with SampleID (stickyCount below).
      {
        field: '__test', header: 'Test', width: 56, align: 'center',
        sortable: false, filterable: false, noExport: true,
        render: s => (
          <button type="button" className="sr-gridlink"
            title={`Tests for ${s.id}`}
            onClick={e => {
              e.stopPropagation();
              setActive(samples.findIndex(x => x.id === s.id));
              setTestPopup(s.id);
            }}>Test</button>
        ),
      },
      {
        field: '__cont', header: 'Cont', width: 56, align: 'center',
        sortable: false, filterable: false, noExport: true,
        render: s => (
          <button type="button" className="sr-gridlink"
            title={`Containers for ${s.id}`}
            onClick={e => {
              e.stopPropagation();
              setActive(samples.findIndex(x => x.id === s.id));
              setContPopup(s.id);
            }}>Cont</button>
        ),
      },
      {
        field: 'id', header: 'SampleID', width: 150,
        render: s => (
          <span style={{ fontWeight: 600 }}>
            {s.id} <span className="sr-hint-inline" style={{ display: 'inline' }}>({s.tests.length})</span>
          </span>
        ),
        text: s => s.id,
      },
    ];

    // ReceivedDate is a FK to SC_ReceivedDates (a dropdown of the job's receipt
    // dates), not a plain text field. Build it as a reusable column so it can be
    // placed at its Sample Matrix Field Setup SortOrder position (user request
    // 2026-09-29 — it was previously always appended at the far right, out of order).
    let rdAdded = false;
    // ReceivedDate shows the DATE + TIME (MM/DD/YYYY HH:mm) like ALPACA — the receipt
    // carries a meaningful time (e.g. 04:25), so it is a datetime column, not date-only
    // (user request 2026-10-06). All five spots that format a receipt date use
    // fmtLocalDateTime so the combo label, cell text, export and the label->Oid
    // resolvers stay in lock-step.
    const rdLabel = (oid: string) =>
      receivedDates.find(r => r.oid === oid)
        ? fmtLocalDateTime(receivedDates.find(r => r.oid === oid)!.date)
        : '';
    const makeReceivedDateCol = (): AppColumn<SrSample> => ({
      field: 'ReceivedDate', header: 'ReceivedDate', width: 150,
      value: s => rdLabel(s.v['ReceivedDate'] ?? ''),
      text:  s => rdLabel(s.v['ReceivedDate'] ?? ''),
      edit: {
        type: 'combo',
        // 'N/A' first (legacy parity) + a formatted option per receipt row.
        options: ['N/A', ...receivedDates.map(r => fmtLocalDateTime(r.date))],
      },
      onCellContextMenu: (e, s) => {
        const value = rdLabel(s.v['ReceivedDate'] ?? '');
        if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
        setCtx({ col: 'ReceivedDate', label: 'ReceivedDate', value, x: e.clientX, y: e.clientY });
      },
    });

    if (config) {
      // ── Dynamic columns driven by dbo.SampleMatrixSetupFields ────────────
      // Field type and lookup source come from the SP (RS1 FieldType /
      // LookupSource) — no hardcoded FK_LOOKUP / BOOL_FIELDS / DATE_FIELDS /
      // RO_FIELDS constants.
      for (const f of orderedFields) {
        const { fieldId, caption, width, fieldType, lookupSource, isReadOnly } = f;
        // Date Collected is never flagged required in the grid (user report 2026-10-08):
        // it is unknown at registration (esp. Pre-scheduled) and legacy accepts it empty,
        // so ignore the matrix-config Mandatory flag for it (no red * / yellow tint).
        const isMandatory = f.isMandatory && fieldId !== 'CollectDate' && fieldId !== 'DateCollected';
        // Mandatory column header: append a red asterisk (§18.6 "required-yet-empty = yellow tint + red *").
        const hdr = isMandatory
          ? <><span>{caption}</span><span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span></>
          : caption;

        // SampleID is already rendered as the frozen first column.
        if (fieldId === 'SampleID') continue;

        // ReceivedDate is a special SC_ReceivedDates FK dropdown — render it HERE
        // at its Sample Matrix Field Setup SortOrder position (not appended at the
        // far right). The fallback append below only fires if the matrix config
        // omits it (user request 2026-09-29).
        if (fieldId === 'ReceivedDate') { out.push(makeReceivedDateCol()); rdAdded = true; continue; }

        // Translate XAF FieldID → the SrSample.v[] key used throughout this
        // module (SL_COLS-compatible names so static and dynamic paths share
        // the same v[] slots and the save body works for both cases).
        const vKey = FIELD_ID_TO_V_KEY[fieldId] ?? fieldId;

        // The SP may return both a legacy alias and the canonical XAF FieldID
        // that resolve to the same vKey — e.g. LoginBy + EnteredBy both → the
        // 'EnteredBy' slot, LoginDateTime + EnteredDate → 'EnteredDate'. Keep the
        // first occurrence (one column), but when the CANONICAL field arrives
        // (its FieldID equals the vKey, e.g. 'EnteredBy'), adopt its caption so
        // the header matches the slot/data — otherwise the column shows the alias
        // caption ("LoginBy"/"LoginDateTime") and the user sees EnteredBy/
        // EnteredDate as "missing" (user request 2026-09-26).
        const dupIdx = out.findIndex(c => c.field === vKey);
        if (dupIdx >= 0) {
          if (fieldId === vKey) { out[dupIdx]!.header = hdr; out[dupIdx]!.headerText = caption; }
          continue;
        }

        // AlternativeStation: a POPUP site-picker (G5 "Alternative Station"
        // window parity, user request 2026-09-26) — NOT an inline text cell.
        // Clicking the cell opens the StationPickerDialog (client's Sample Sites
        // grid); OK sets AlternativeStation (name) + AlternativeStationOid (Oid).
        if (vKey === 'AlternativeStation') {
          out.push({
            field: vKey, header: hdr, headerText: caption, width: width || 170,
            value: s => s.v['AlternativeStation'] ?? '',
            render: s => (
              <button
                type="button"
                onClick={e => { e.stopPropagation(); setStationPick(s.id); }}
                title="Pick alternative station"
                style={{
                  width: '100%', textAlign: 'left', background: 'transparent',
                  border: 'none', padding: '0 2px', cursor: 'pointer', font: 'inherit',
                  color: s.v['AlternativeStation'] ? 'var(--ai-ink)' : 'var(--ai-muted)',
                }}
              >
                {s.v['AlternativeStation'] || 'Select…'}
              </button>
            ),
          });
          continue;
        }

        if (fieldType === 'bool') {
          const statusDriven = vKey === 'SubOut';
          const boolDisabled = statusDriven || isReadOnly;
          out.push({
            field: vKey, header: hdr, headerText: caption, width,
            align: 'center', sortable: false, filterable: false,
            render: s => (
              <input type="checkbox"
                checked={s.v[vKey] === '1'}
                disabled={boolDisabled}
                aria-label={`${caption} ${s.id}`}
                title={statusDriven ? "Driven by SampleStatus — set status to 'Sub Out' to activate" : undefined}
                onClick={e => e.stopPropagation()}
                onChange={boolDisabled ? undefined : e => {
                  const chk = e.target.checked;
                  mutate(draft => {
                    const t = draft.find(x => x.id === s.id);
                    if (t) t.v[vKey] = chk ? '1' : '0';
                  });
                }} />
            ),
          });
        } else if (fieldType === 'dropdown') {
          const opts = getLookupOpts(config, lookupSource);
          const col: AppColumn<SrSample> = {
            field: vKey, header: hdr, headerText: caption, width,
            value: s => fkName(opts, s.v[vKey] ?? ''),
            text:  s => fkName(opts, s.v[vKey] ?? ''),
          };
          // G5 parity (user request 2026-09-26): SiteName (StationLocation) is
          // the site-PICKER that drives the site-attribute auto-fill cascade, and
          // legacy keeps it editable even though dbo.SamplingFieldConfiguration
          // marks it IsReadonly=True for some matrices. Force its combo editor so
          // clicking opens the site list (other read-only fields are unaffected).
          if (!isReadOnly || vKey === 'SiteName') {
            col.edit = {
              type: 'combo',
              // ALPACA parity (user report 2026-10-09): a non-mandatory lookup combo
              // (e.g. SampleStatus) shows the clear "✕" so a value, once set, can be
              // emptied again — legacy's DevExpress combo allows clearing it. Required
              // (*) dropdowns stay non-clearable so they can't be left blank.
              clearable: !isMandatory,
              // SampleType cascade: filter by the row's VisualMatrix
              options: lookupSource === 'sampleTypes'
                ? (row: SrSample) => {
                    const vmOid = row.v['SampleMatrix'] ?? '';
                    return config.sampleTypes
                      .filter(st =>
                        !vmOid ||
                        !st.visualMatrixGuids ||
                        st.visualMatrixGuids.toUpperCase().includes(vmOid.toUpperCase()))
                      .map(st => st.name);
                  }
                // RejectionCriteria cascade: filter by the row's VisualMatrix
                : lookupSource === 'rejectionCriteria'
                ? (row: SrSample) => {
                    const vmOid = row.v['SampleMatrix'] ?? '';
                    return config.rejectionCriteria
                      .filter(rc =>
                        !vmOid ||
                        !rc.visualMatrixGuids ||
                        rc.visualMatrixGuids.toUpperCase().includes(vmOid.toUpperCase()))
                      .map(rc => rc.name);
                  }
                // Collectors: dbo.Collector can hold several rows that resolve to the
                // SAME display name, so the dropdown showed each name twice (user
                // report 2026-10-06). Show each name ONCE here; the full `opts` are
                // still used to resolve an existing row's Collector Oid -> name.
                : lookupSource === 'collectors'
                // Legacy parity (SampleLogIn.CollectorDataSource): when the job's header
                // has Collector(s) chosen, the grid dropdown offers ONLY those — not
                // every collector (user report 2026-10-06).
                ? Array.from(new Set(opts.map(o => o.name)))
                    .filter(n => !headerCollectorNames?.length || headerCollectorNames.includes(n))
                : opts.map(o => o.name),
              requiredEmpty: isMandatory ? (s: SrSample) => !s.v[vKey] : undefined,
            };
            col.onCellContextMenu = (e, s) => {
              const value = fkName(opts, s.v[vKey] ?? '');
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            };
          }
          out.push(col);
        } else if (fieldType === 'time') {
          // Time-only field — a native HH:mm picker (Bug: TimeCollected used to
          // be a plain text cell that happily accepted "testing").
          const col: AppColumn<SrSample> = {
            field: vKey, header: hdr, headerText: caption, width,
            value: s => s.v[vKey] ?? '',
            text:  s => s.v[vKey] ?? '',
          };
          if (!isReadOnly) {
            col.edit = {
              type: 'time',
              requiredEmpty: isMandatory ? (s: SrSample) => !s.v[vKey] : undefined,
            };
            col.onCellContextMenu = (e, s) => {
              const value = s.v[vKey] ?? '';
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            };
          }
          out.push(col);
        } else if (fieldType === 'phone') {
          // Phone field (e.g. CollectorPhone) — digits with optional
          // separators/extension; rejects arbitrary free text.
          const col: AppColumn<SrSample> = {
            field: vKey, header: hdr, headerText: caption, width,
            value: s => s.v[vKey] ?? '',
            text:  s => s.v[vKey] ?? '',
          };
          if (!isReadOnly) {
            col.edit = {
              type: 'text',
              pattern: String.raw`[0-9()+\-.\s]{7,20}`,
              patternMessage: 'Enter a valid phone number (digits, spaces, ( ) + - . only).',
              requiredEmpty: isMandatory ? (s: SrSample) => !s.v[vKey] : undefined,
            };
            col.onCellContextMenu = (e, s) => {
              const value = s.v[vKey] ?? '';
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            };
          }
          out.push(col);
        } else if (fieldType === 'number') {
          // Numeric field — text input, display raw value
          const col: AppColumn<SrSample> = {
            field: vKey, header: hdr, headerText: caption, width,
            value: s => s.v[vKey] ?? '',
            text:  s => s.v[vKey] ?? '',
          };
          if (!isReadOnly) {
            col.edit = {
              type: 'text',
              requiredEmpty: isMandatory ? (s: SrSample) => !s.v[vKey] : undefined,
            };
            col.onCellContextMenu = (e, s) => {
              const value = s.v[vKey] ?? '';
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            };
          }
          out.push(col);
        } else {
          // text / date / datetime / ro
          const isDate     = fieldType === 'date';
          const isDatetime = fieldType === 'datetime';
          const isRO       = fieldType === 'ro' || isReadOnly;

          const renderText = (raw: string) => {
            if (isDate)     return toDateInput(raw);
            if (isDatetime) return raw ? fmtLocalDateTime(raw) : '';
            return raw;
          };
          const col: AppColumn<SrSample> = {
            field: vKey, header: hdr, headerText: caption, width,
            value: s => renderText(s.v[vKey] ?? ''),
            text:  s => renderText(s.v[vKey] ?? ''),
          };
          if (!isRO && !isDatetime) {
            col.edit = {
              type: isDate ? 'date' : 'text',
              requiredEmpty: isMandatory ? (s: SrSample) => !s.v[vKey] : undefined,
            };
            col.onCellContextMenu = (e, s) => {
              const value = s.v[vKey] ?? '';
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            };
          }
          out.push(col);
        }
      }

      // Add static boolean Hold only when matrix config does NOT include it
      // (the Hold FieldID in config takes precedence)
      const hasHoldField = config.fields.some(f => f.fieldId === 'Hold');
      if (!hasHoldField) {
        out.push(staticHoldCol(mutate, samples));
      }
    } else {
      // ── Static fallback when config hasn't loaded yet (SL_COLS) ──────────
      for (const [key, type, width] of SL_COLS) {
        // ReceivedDate is always handled by the dedicated FK dropdown block below.
        // Rendering it here would show the raw SC_ReceivedDates OID (a GUID).
        if (key === 'ReceivedDate') continue;
        const readOnly = type === 'ro';
        const col: AppColumn<SrSample> = {
          field: key, header: key, width,
          value: s => s.v[key] ?? '',
          text: s => (type === 'date' ? toDateInput(s.v[key] ?? '') : (s.v[key] ?? '')),
        };
        if (!readOnly) {
          col.edit = {
            type: type === 'status' ? 'combo'
              : type === 'qty' ? 'combo'
                : type === 'date' ? 'date' : 'text',
            options: type === 'status' ? SAMPLE_STATUS_OPTS
              : type === 'qty' ? ['1', '2', '3', '4', '5', '6'] : undefined,
          };
          col.onCellContextMenu = (e, s) => {
            const value = s.v[key] ?? '';
            if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
            setCtx({ col: key, label: key, value, x: e.clientX, y: e.clientY });
          };
        }
        out.push(col);
      }
      out.push(staticHoldCol(mutate, samples));
    }

    // ── ReceivedDate fallback ────────────────────────────────────────────────
    // Append ReceivedDate at the end ONLY if it was NOT already placed at its
    // config SortOrder position above (a matrix whose SetupFields omit it, or the
    // static SL_COLS fallback path). Legacy ALWAYS shows the ReceivedDate column
    // (with an N/A option even before any receipt row exists), so it must still
    // appear when unconfigured — just no longer forced to the far right when the
    // matrix DOES configure it (user request 2026-09-29).
    if (!rdAdded) out.push(makeReceivedDateCol());

    // Final safety net: deduplicate by field (keep first occurrence).
    // Guards against matrix configs that list both a legacy alias (e.g. 'SiteName')
    // and the canonical XAF FieldID (e.g. 'StationLocation') for the same field —
    // both resolve to the same vKey via FIELD_ID_TO_V_KEY.
    const seen = new Set<string>();
    const deduped = out.filter(c => {
      if (seen.has(c.field)) return false;
      seen.add(c.field);
      return true;
    });
    // Qty sits right next to Cont (owner request 2026-10-15) so the bottle count is
    // visible beside the link that assigns bottle IDs. stickyCount accounts for it.
    const qi = deduped.findIndex(c => c.field === 'Qty');
    const ci = deduped.findIndex(c => c.field === '__cont');
    if (qi > ci && ci >= 0) {
      const [qcol] = deduped.splice(qi, 1);
      deduped.splice(ci + 1, 0, qcol!);
    }
    return deduped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, orderedFields, samples, active, mutate, onWarn, receivedDates, setActive, setTestPopup, setContPopup, headerCollectorNames]);

  // ── Find Column locator ───────────────────────────────────────────────────
  const columnNames = useMemo(
    () => config
      ? ['SampleID', ...orderedFields.map(f => f.caption)]
      : ['SampleID', ...SL_COLS.map(c => c[0]), 'Hold'],
    [config, orderedFields],
  );

  // ── Toolbar actions ───────────────────────────────────────────────────────
  const addSample = () => {
    mutate(draft => {
      const template = draft[active] ?? draft[0];
      const s: SrSample = {
        id: '', hold: false,
        v: { Qty: '1', SampleMatrix: template?.v.SampleMatrix ?? '' },
        tests: [],
      };
      autoBottles(s);
      draft.push(s);
      renumber(draft, jobId);
    });
    onSuccess('Sample added.');
  };

  // Delete the CHECKED samples (or the active row when none are ticked), mirroring
  // ALPACA's SampleLogIn delete (DeleteValidationViewController.cs):
  //   • NO "at least one sample is required" guard — that check is COMMENTED OUT in
  //     legacy; a job may have ALL its samples deleted, which the SP then resets to
  //     Pending Submit (legacy sets Status = PendingSubmit when the last one goes).
  //   • Per-sample blockers surface the legacy message "The sample cannot be deleted!
  //     it is attached to test."; deletable samples are removed and the survivors'
  //     SampleNo is resequenced 1..N server-side.
  const deleteCurrent = () => {
    const targets = sel.size > 0
      ? samples.filter(s => sel.has(s.id))
      : (samples[active] ? [samples[active]] : []);
    if (targets.length === 0) { onWarn('Please select atleast one record to proceed.'); return; }

    const label = targets.length === 1 ? targets[0]!.id : `${targets.length} samples`;

    confirmAction('Delete sample', `Delete ${label}? This cannot be undone.`, async () => {
      const oids = targets
        .map(s => s.v?.['_oid'] as string | undefined)
        .filter((o): o is string => !!o);

      if (jobOid && oids.length > 0) {
        try {
          const { data } = await api.delete(
            `/api/v1/sample-registration/${jobOid}/samples`,
            { data: { sampleOids: oids } },
          );
          if (data?.success) {
            const d = data.data;
            const deleted = d.samples?.filter((s: any) => s.deleted) ?? [];
            const blocked = d.samples?.filter((s: any) => !s.deleted) ?? [];
            setSel(new Set());
            if (deleted.length > 0) { onSuccess('Deleted successfully.'); onServerReload?.(); }
            if (blocked.length > 0) {
              onWarn(blocked[0].blockReason ?? 'The sample cannot be deleted! it is attached to test.');
            }
          } else {
            onWarn(data?.error?.message ?? 'Delete failed.');
          }
        } catch (e: any) {
          onWarn(e?.response?.data?.error?.message ?? e?.message ?? 'Delete failed.');
        }
        return;
      }

      // Unsaved job (rows have no server Oid yet): remove the targets client-side.
      const idset = new Set(targets.map(t => t.id));
      mutate(draft => {
        for (let i = draft.length - 1; i >= 0; i--) if (idset.has(draft[i]!.id)) draft.splice(i, 1);
        renumber(draft, jobId);
      });
      setSel(new Set());
      setActive(0);
      onSuccess('Deleted successfully.');
    }, true);
  };

  // `count` comes from the dialog (its spinner owns the number since the dialog
  // was extracted to SampleLoginDialogs); it falls back to the local state the
  // keyboard shortcut still sets.
  // Re-entry guard: OK clicked several times used to fire several overlapping copy
  // requests whose responses were appended in COMPLETION order (SampleIDs out of
  // sequence, user report 2026-10-07). One copy at a time; the dialog closes on the
  // first click.
  const copyBusyRef = useRef(false);
  const runCopySamples = async (count?: number) => {
    if (copyBusyRef.current) return;
    copyBusyRef.current = true;
    setCopyOpen(false);
    try { await runCopySamplesOnce(count); } finally { copyBusyRef.current = false; }
  };
  const runCopySamplesOnce = async (count?: number) => {
    // Source = the first CHECKED sample (ALPACA parity), falling back to the
    // marked row only if nothing is ticked (should not happen — openCopySamples
    // gates on a selection first).
    const srcIdx = sel.size > 0 ? samples.findIndex(s => sel.has(s.id)) : active;
    const src = samples[srcIdx];
    if (!src) { onWarn('Please select an item to proceed further.'); return; }
    const n = Math.max(1, count ?? copyCount);

    // For persisted jobs with a server-assigned sample Oid, use the server-side
    // SP which generates race-safe SampleNo/SampleID and persists immediately.
    if (jobOid && src.v['_oid']) {
      try {
        const { data } = await api.post(`/api/v1/sample-registration/${jobOid}/copy-samples`, {
          sourceSampleOid: src.v['_oid'], copyCount: n,
        });
        if (data?.success) {
          // Append the new rows IN MEMORY, carrying the server-assigned SampleID /
          // Oid from the SP's response. Deliberately NOT a full job reload: the
          // reload is the slow part of this action AND it discards in-memory state,
          // including tests the user just assigned but has not saved — which is what
          // emptied the test grid (user report 2026-10-03). Cloning the in-memory
          // source keeps them; the page Save reconciles the rows (replace-all).
          const created = (data.data?.newSamples ?? []) as
            { oid: string; sampleNo: number; sampleID: string }[];
          if (created.length > 0) {
            mutate(draft => {
              const source = draft[srcIdx];
              if (!source) return;
              for (const c of [...created].sort((a, b) => a.sampleNo - b.sampleNo)) {
                draft.push({
                  id: c.sampleID,
                  hold: source.hold,
                  // ClientSampleID (SampleName) is left blank on a copy — legacy
                  // CopySamples_Accepting never carries it over.
                  v: { ...source.v, SampleName: '', _oid: c.oid },
                  tests: source.tests.map(cloneTest),
                });
              }
              // Keep the grid in SampleID (sample number) order.
              const no = (x: SrSample) => Number(/-(d+)$/.exec(x.id)?.[1] ?? NaN);
              if (draft.every(x => !Number.isNaN(no(x)))) draft.sort((a, b) => no(a) - no(b));
            });
          } else {
            onServerReload?.();   // older API shape — fall back to a reload
          }
          setCopyOpen(false);
          onSuccess(`${n} cop${n > 1 ? 'ies' : 'y'} of ${src.id} created.`);
        } else {
          onWarn(data?.error?.message ?? 'Copy failed.');
        }
      } catch (e: any) {
        onWarn(e?.response?.data?.error?.message ?? e?.message ?? 'Copy failed.');
      }
      return;
    }

    // Client-side copy for new (unsaved) jobs
    mutate(draft => {
      const source = draft[srcIdx];
      if (!source) return;
      const clones = Array.from({ length: n }, () => ({
        id: '', hold: source.hold,
        // Every field copies verbatim EXCEPT ClientSampleID (v.SampleName) —
        // legacy SampleLogInViewController.CopySamples_Accepting leaves it
        // blank on the new rows (lines ~1001/1086), never copies or derives it.
        v: { ...source.v, SampleName: '' },
        tests: source.tests.map(cloneTest),
      }));
      draft.splice(srcIdx + 1, 0, ...clones);
      renumber(draft, jobId);
    });
    setCopyOpen(false);
    onSuccess(`${n} cop${n > 1 ? 'ies' : 'y'} of ${src.id} added.`);
  };

  // Base frozen columns: ▶ indicator + Test + Cont + SampleID = 4 (the two row-link
  // columns and SampleID stay pinned, ALPACA parity). On top of those, EVERY
  // Freeze=true matrix field is pinned — G5 sets FixedStyle=Left per freeze column
  // regardless of position (see orderedFields above, which moves them to the front),
  // so the sticky region = base 4 + the freeze-field count. SampleID (SortOrder 1)
  // is the base frozen column and is excluded so it is not double-counted (the
  // 2026-09-29 "SiteName frozen with its Freeze off" regression came from that
  // double-count; the exclusion below keeps it fixed).
  const stickyCount = useMemo(() => {
    // Qty is moved next to Cont (ahead of SampleID), so it joins the sticky region;
    // when it is itself a Freeze field it is already counted below.
    const qtyField = config?.fields.find(f => f.fieldId === 'Qty');
    const qtyShift = (!config || (qtyField && !qtyField.freeze)) ? 1 : 0;
    if (!config) return 4 + qtyShift;
    return 4 + qtyShift + config.fields.filter(f => f.freeze && f.fieldId !== 'SampleID').length;
  }, [config]);

  const exportMatrix = useMemo(() => {
    const rdLabel = (oid: string) => {
      const rd = receivedDates.find(r => r.oid === oid);
      return rd ? fmtLocalDateTime(rd.date) : '';
    };
    if (config) {
      return {
        headers: ['SampleID', ...orderedFields.map(f => f.caption), ...(receivedDates.length > 0 ? ['ReceivedDate'] : [])],
        rows: samples.map(s => [
          s.id,
          ...orderedFields.map(f => {
            const vKey = FIELD_ID_TO_V_KEY[f.fieldId] ?? f.fieldId;
            if (f.fieldType === 'dropdown' && f.lookupSource) {
              const opts = getLookupOpts(config, f.lookupSource);
              return fkName(opts, s.v[vKey] ?? '');
            }
            if (f.fieldType === 'bool') return s.v[vKey] === '1' ? 'Yes' : 'No';
            return s.v[vKey] ?? '';
          }),
          ...(receivedDates.length > 0 ? [rdLabel(s.v['ReceivedDate'] ?? '')] : []),
        ]),
      };
    }
    return {
      headers: ['SampleID', ...SL_COLS.map(c => c[0]), 'Hold', ...(receivedDates.length > 0 ? ['ReceivedDate'] : [])],
      rows: samples.map(s => [
        s.id, ...SL_COLS.map(c => s.v[c[0]] ?? ''), s.hold ? 'Yes' : 'No',
        ...(receivedDates.length > 0 ? [rdLabel(s.v['ReceivedDate'] ?? '')] : []),
      ]),
    };
  }, [config, orderedFields, samples, receivedDates]);

  // Named action handlers so the toolbar buttons AND the keyboard shortcuts share
  // one implementation (never a raw listener — the ONE shared hotkey layer, §18.6).
  const openCopySamples = () => {
    // ALPACA parity (user report 2026-10-03): Copy Samples acts on the CHECKED
    // sample — clicking it with nothing ticked shows the legacy validation.
    if (sel.size === 0) { onWarn('Please select an item to proceed further.'); return; }
    setCopyCount(1); setCopyOpen(true);
  };
  const openCopyTest = () => {
    // ALPACA parity (SL_CopyTest_Executing): exactly ONE source sample must be
    // CHECKED — 0 or >1 shows the legacy validation.
    if (sel.size === 0) { onWarn('Please select an item to proceed further.'); return; }
    if (sel.size > 1) { onWarn('Please select only one item to proceed further.'); return; }
    if (samples.length < 2) { onWarn('Add at least two samples before copying tests between them.'); return; }
    setCopyTestOpen(true);
  };
  const openAddSite = () => setSiteOpen(true);
  const openTestEdit = () => {
    // Bug: this used to unconditionally jump to the "Test View" tab (a
    // flat, all-samples list) regardless of which sample was checked —
    // legacy ALPACA's "Test Edit" opens the test grid for the CHECKED
    // sample. Route to that sample's Test Assignment panel instead
    // (below, on this same Sample Login tab) so checking a row and
    // clicking Test Edit actually edits that sample's tests.
    if (sel.size === 0) { onWarn('Check a sample first, then click Test Edit.'); return; }
    const firstId = samples.find(s => sel.has(s.id))?.id;
    const idx = firstId ? samples.findIndex(s => s.id === firstId) : -1;
    if (idx >= 0) setActive(idx);
    // ALPACA parity (user report 2026-10-06): Test Edit opens the "Test Edit"
    // POPUP (the Test Assignment window) for the checked sample — it no longer
    // just scrolls to the inline Test Assignment panel.
    // Legacy "Test Edit" popup: Sample ID, Add/Remove Test, Copy Tests, test grid.
    if (firstId) setTestEditId(firstId);
  };
  const reanalysisDisabled = !jobOid || !samples[active]?.v['_oid'];
  const runReanalysis = () => {
    const src = samples[active];
    if (!jobOid || !src?.v['_oid']) return;
    confirmAction('Reanalysis', `Create a reanalysis sample from ${src.id}?`, async () => {
      try {
        const { data } = await api.post(`/api/v1/sample-registration/${jobOid}/reanalysis`, {
          sourceSampleOid: src.v['_oid'],
        });
        if (data?.success) {
          onSuccess(`Reanalysis sample ${data.data.sampleID} created (${data.data.sysSampleCode}).`);
          onServerReload?.();
        } else {
          onWarn(data?.error?.message ?? 'Reanalysis failed.');
        }
      } catch (e: any) {
        onWarn(e?.response?.data?.error?.message ?? e?.message ?? 'Reanalysis failed.');
      }
    });
  };

  // Sample Login workspace shortcuts (CLAUDE.md standing rule 14, §18.6). Registered
  // by this bespoke form next to its buttons; active only while the Samples step is
  // shown. Reserved verbs use their canonical keys (Add Sample = New Alt+N, Delete
  // Alt+D, Test Edit = Edit Alt+E, Export Alt+X); the form-specific actions take
  // free keys (Add Collector Alt+O, Add Site Alt+T) and the "copy"/reanalysis
  // extras take Alt+Shift combos so they never collide with the reserved single-key
  // verbs. All are remappable via Settings → Keyboard Shortcuts.
  useHotkeys([
    { combo: 'alt+n', label: 'Add Sample', group: 'Samples', order: 1, run: addSample },
    { combo: 'alt+d', label: 'Delete Sample', group: 'Samples', order: 2, run: deleteCurrent },
    { combo: 'alt+o', label: 'Add Collector', group: 'Samples', order: 3, run: () => onNewCollector() },
    { combo: 'alt+t', label: 'Add Site', group: 'Samples', order: 4, run: openAddSite },
    { combo: 'alt+shift+s', label: 'Copy Samples', group: 'Samples', order: 5, run: openCopySamples },
    { combo: 'alt+shift+t', label: 'Copy Test', group: 'Samples', order: 6, run: openCopyTest },
    { combo: 'alt+e', label: 'Test Edit', group: 'Samples', order: 7, run: openTestEdit },
    { combo: 'alt+shift+r', label: 'Reanalysis', group: 'Samples', order: 8, disabled: reanalysisDisabled, run: runReanalysis },
    { combo: 'alt+x', label: 'Export to', group: 'Samples', order: 9, run: () => exportRef.current?.open() },
  ]);

  return (
    <div className="sr-sl-layout">
      <div className="sr-sl-panel sr-sl-samples">
      <div className="sr-subtoolbar" style={{ marginBottom: 8 }}>
        {reportItems.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button className="ca-btn" onClick={() => setReportsOpen(o => !o)}>
              <span className="tb-i tb-i-blue">📄</span> Reports ▾
            </button>
            {reportsOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setReportsOpen(false)} />
                <div className="sr-colfind-menu" style={{ top: 34, left: 0, minWidth: 170, zIndex: 41 }}>
                  {reportItems.map(r => (
                    <button key={r.id} className="sr-colfind-item" title={r.templateName}
                      onClick={() => void previewReport(r)}>{r.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <span className="app-toolbar__divider" aria-hidden="true" />
        <button className="ca-btn" onClick={deleteCurrent} title={`Delete (${formatCombo('alt+d')})`}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={addSample} title={`Add Sample (${formatCombo('alt+n')})`}><span className="tb-i tb-i-green">＋</span> Add Sample</button>
        <button className="ca-btn" onClick={onNewCollector} title={`Add Collector (${formatCombo('alt+o')})`}>
          <span className="tb-i tb-i-green">＋</span> Add Collector
        </button>
        <button className="ca-btn" onClick={openAddSite} title={`Add Site (${formatCombo('alt+t')})`}>
          <span className="tb-i tb-i-green">＋</span> Add Site
        </button>
        <span className="app-toolbar__divider" aria-hidden="true" />
        <button className="ca-btn" onClick={openCopySamples} title={`Copy Samples (${formatCombo('alt+shift+s')})`}>
          <span className="tb-i tb-i-blue">🗎</span> Copy Samples
        </button>
        <button className="ca-btn" onClick={openCopyTest} title={`Copy Test (${formatCombo('alt+shift+t')})`}>
          <span className="tb-i tb-i-blue">🗎</span> Copy Test
        </button>
        <button className="ca-btn" onClick={openTestEdit} title={`Test Edit (${formatCombo('alt+e')})`}>
          <span className="tb-i tb-i-blue">✎</span> Test Edit
        </button>
        <button className="ca-btn"
          disabled={reanalysisDisabled}
          title={jobOid ? `Create a reanalysis (LR) sample (${formatCombo('alt+shift+r')})` : 'Save the job first'}
          onClick={runReanalysis}
        >Reanalysis</button>
        <span className="app-toolbar__divider" aria-hidden="true" />
        <ExportMenu ref={exportRef} filename={`sample-login-${jobId || 'new'}`} {...exportMatrix} triggerClassName="ca-btn" />
      </div>

      {/* Find Column — the grid is 30+ columns wide. Shared component since
          2026-09-23 so Sample Transfer's Sample Login uses the SAME one
          (CLAUDE.md rule 8); the inline copy that lived here was removed. */}
      <ColumnFinder columns={columnNames} gridRef={scrollRef} />

      <div ref={scrollRef} className="sr-sl-grid-wrap">
        <AppDataGrid<SrSample>
          rows={samples}
          columns={cols}
          getRowId={s => s.id}
          selectable
          selectedIds={sel}
          onSelectionChange={setSel}
          stickyColumns={stickyCount}
          // Columns AUTO-SIZE to fit their (single-line) header and content, like
          // ALPACA's Sample Login grid — the configured Sample Matrix Field Setup
          // Width is a MINIMUM hint, not a hard cap. `enforceWidths` was pinning the
          // small stored widths (Qty 50, DateCollected/TimeCollected 75-90, …) so
          // headers clipped to "Sa…"/"Alte…"; wrapping them instead made the header
          // row tall and fragmented ("Altern/ativeS/tation"). Neither matched
          // ALPACA, which shows wider single-line columns. So both enforceWidths and
          // wrapHeaders are OFF: the auto table-layout grows each column to its
          // header width (single line), reproducing ALPACA. Columns stay
          // drag-resizable. (user report 2026-10-05; reverses the 2026-09-29
          // enforceWidths/G5-exact-width change for THIS grid.)
          onRowClick={s => setActive(samples.findIndex(x => x.id === s.id))}
          onCellCommit={(row, col, value) => {
            // ReceivedDate column: combo displays formatted date, store SC_ReceivedDates.Oid
            if (col.field === 'ReceivedDate') {
              const rd = receivedDates.find(r => fmtLocalDateTime(r.date) === value);
              if (rd) rdClearedRef.current.delete(String(row.id)); else rdClearedRef.current.add(String(row.id));
              setCell(row.id, 'ReceivedDate', rd?.oid ?? '');
              return;
            }
            // When the config is loaded, FK combo edits emit the display name;
            // resolve it back to the OID before storing so the render and save
            // path both work with consistent OID values.
            if (config) {
              const fCfg = config.fields.find(fc => (FIELD_ID_TO_V_KEY[fc.fieldId] ?? fc.fieldId) === col.field);
              if (fCfg?.fieldType === 'dropdown' && fCfg.lookupSource) {
                const opts = getLookupOpts(config, fCfg.lookupSource);
                const found = opts.find(o => o.name === value);
                // When a site is chosen, auto-fill all site attribute columns
                // (legacy SampleRegistrationViewController StationLocation callback).
                if (col.field === 'SiteName' && found) {
                  mutate(draft => {
                    const s = draft.find(x => x.id === row.id);
                    if (!s) return;
                    applySiteFill(s, found);
                  });
                  return;
                }
                setCell(row.id, col.field, found?.oid ?? value);
                return;
              }
            }
            setCell(row.id, col.field, value);
          }}
          defaultPageSize={100}
          emptyText="No samples yet"
        />
      </div>

      {ctx && (
        <CellContextMenu
          value={ctx.value}
          columnLabel={ctx.label}
          selectedCount={sel.size}
          position={{ x: ctx.x, y: ctx.y }}
          // ALPACA parity (SampleRegistrationViewController "Copy To All Cell"): the
          // single copy action copies the focused cell ONLY to the checkbox-SELECTED
          // rows — never to unselected rows (its name is a legacy misnomer). So this
          // requires a selection and copies to selected only (user report 2026-09-30:
          // "Copy to All Cells copied to all rows even when the checkbox was not
          // selected"). The separate "Copy to Selected Rows" item is dropped — legacy
          // has ONE option, and it already means selected-rows-only.
          onCopyAll={() => {
            if (sel.size === 0) { onWarn('Tick at least one row first.'); return; }
            copyColumn(ctx.col, ctx.value, true);
          }}
          onClose={() => setCtx(null)}
        />
      )}

      </div>{/* end sr-sl-samples */}

      {/* Test Assignment for the current record */}
      <div ref={testPanelRef} className="sr-sl-panel sr-sl-tests">
      <TestAssignment
        jobId={jobId} samples={samples} setSamples={setSamples} active={active}
        onSuccess={onSuccess}
        unitOptions={unitOptions}
        onEdit={() => { const s = samples[active]; if (s) setTestPopup(s.id); }}
        onOpenContainers={testKey => { const s = samples[active]; if (s) { setContTestKey(testKey); setContPopup(s.id); } }}
      />
      </div>{/* end sr-sl-tests */}

      {/* Copy Samples */}
      {copyOpen && (
        <CopySamplesDialog
          sampleId={samples.find(s => sel.has(s.id))?.id ?? samples[active]?.id ?? ''}
          onClose={() => setCopyOpen(false)}
          onApply={n => void runCopySamples(n)}
        />
      )}

      {testEditId && !copyTestOpen && (
        <TestEditDialog
          samples={samples}
          initialId={testEditId}
          matrixName={s => { const raw = s.v.SampleMatrix ?? ''; return matrixMap?.[raw.toLowerCase()] ?? matrixMap?.[raw] ?? raw; }}
          testRows={testRows}
          groupChildren={groupChildren}
          jobOid={jobOid}
          onTestsChange={(id, next) => mutate(draft => {
            const s = draft.find(x => x.id === id);
            if (!s) return;
            s.tests = next.map(copyTest);
            autoBottles(s);
          })}
          onCopyTests={id => {
            if (samples.length < 2) { onWarn('Add at least two samples before copying tests between them.'); return; }
            setCopyTestFrom(id); setTestEditId(null); setCopyTestOpen(true);
          }}
          onClose={() => setTestEditId(null)}
          onWarn={onWarn}
        />
      )}

      {/* Copy Tests to Samples */}
      {copyTestOpen && (
        <CopyTestDialog
          // Normalise each sample's matrix to its display NAME before comparing:
          // v.SampleMatrix holds an Oid for server-loaded rows but the display name
          // for client-seeded ones, and SQL CAST yields UPPERCASE Oids while
          // Guid.ToString() yields lowercase — so an exact compare wrongly reported
          // "different Sample Matrix" and hid every target (user report 2026-10-03).
          samples={samples.map(s => {
            const raw = s.v.SampleMatrix ?? '';
            return { id: s.id, matrix: matrixMap?.[raw.toLowerCase()] ?? matrixMap?.[raw] ?? raw };
          })}
          fromId={copyTestFrom ?? samples.find(s => sel.has(s.id))?.id ?? samples[active]?.id ?? ''}
          onClose={() => { setCopyTestOpen(false); setCopyTestFrom(null); }}
          onApply={async (fromId, targetIds) => {
            // Server-side copy for persisted jobs
            const srcSample = samples.find(s => s.id === fromId);
            const srcOid = srcSample?.v['_oid'];
            if (jobOid && srcOid) {
              try {
                let totalCopied = 0;
                for (const tid of targetIds) {
                  const tgt = samples.find(s => s.id === tid);
                  const tgtOid = tgt?.v['_oid'];
                  if (!tgtOid) continue;
                  const { data } = await api.post(`/api/v1/sample-registration/${jobOid}/copy-test`, {
                    sourceSampleOid: srcOid, targetSampleOid: tgtOid,
                  });
                  if (data?.success) totalCopied += data.data.copiedCount;
                }
                // Reflect the copy IN MEMORY (same MERGE as the client path below)
                // instead of a full job reload: the reload is the slow part of this
                // action and it discards in-memory state, including tests just
                // assigned but not yet saved. The page Save reconciles the rows.
                // Report TESTS (what the grid shows), counted from the same comparison the
                // merge below uses. The server's copiedCount is SampleParameter ROWS
                // (analytes — 3 tests can be 7) and is 0 when the source's tests are
                // unsaved, so it is not used for the message.
                const keyOf = (t: SrTest) => `${t.name}|${t.comp || 'Default'}`;
                let mergedCount = 0;
                const srcNow = samples.find(s => s.id === fromId);
                if (srcNow) {
                  for (const s of samples) {
                    if (s.id === fromId || !targetIds.includes(s.id)) continue;
                    const haveNow = new Set(s.tests.map(keyOf));
                    mergedCount += srcNow.tests.filter(t => !haveNow.has(keyOf(t))).length;
                  }
                }
                mutate(draft => {
                  const source = draft.find(s => s.id === fromId);
                  if (!source) return;
                  const key = (t: SrTest) => `${t.name}|${t.comp || 'Default'}`;
                  for (const s of draft) {
                    if (s.id === fromId || !targetIds.includes(s.id)) continue;
                    const have = new Set(s.tests.map(key));
                    const toAdd = source.tests.filter(t => !have.has(key(t))).map(cloneTest);
                    if (toAdd.length === 0) continue;
                    s.tests = [...s.tests, ...toAdd];
                    autoBottles(s);
                  }
                });
                setCopyTestOpen(false);
                onSuccess(`Tests copied from ${fromId} to ${targetIds.length} sample(s) (${mergedCount} tests).`);
              } catch (e: any) {
                onWarn(e?.response?.data?.error?.message ?? e?.message ?? 'Copy test failed.');
              }
              return;
            }
            // Client-side copy for new (unsaved) jobs — MERGE, never REPLACE.
            // ALPACA / the server SP ADD the source's tests the target doesn't
            // already have (skip duplicates); replacing s.tests wiped a target's
            // own tests whenever the source had fewer/none (user report
            // 2026-10-03: "after copy test the test grid goes empty").
            mutate(draft => {
              const src = draft.find(s => s.id === fromId);
              if (!src) return;
              const key = (t: SrTest) => `${t.name}|${t.comp || 'Default'}`;
              for (const s of draft) {
                if (s.id === fromId || !targetIds.includes(s.id)) continue;
                const have = new Set(s.tests.map(key));
                const toAdd = src.tests.filter(t => !have.has(key(t))).map(cloneTest);
                if (toAdd.length === 0) continue;
                s.tests = [...s.tests, ...toAdd];
                autoBottles(s);
              }
            });
            setCopyTestOpen(false);
            onSuccess(`Tests copied from ${fromId} to ${targetIds.length} sample(s).`);
          }}
          onWarn={onWarn}
        />
      )}

      {/* Add Site */}
      {siteOpen && <AddSiteDialog
        clientOid={clientOid ?? undefined}
        onClose={() => setSiteOpen(false)}
        onAdd={site => {
          setSiteOpen(false);
          // Inject the new site into the config lookup list so it appears immediately
          // in the StationLocation dropdown without a full config reload.
          setConfig(prev => prev ? {
            ...prev,
            sampleSites: [...prev.sampleSites, {
              oid: site.oid, name: site.name,
              visualMatrixGuids: '', address: '', samplingAddress: '',
              keyMap: '', samplePointId: '', samplePointType: '',
              pwsId: '', waterTypeName: '', systemTypeOid: '',
              rejectionCriteriaOid: '', parentSampleID: '', parentSampleDate: '',
            }].sort((a, b) => a.name.localeCompare(b.name)),
          } : prev);
          onSuccess(`Site "${site.name}" added.`);
        }}
        onWarn={onWarn}
      />}

      {/* Alternative Station picker (G5 popup parity) — opened from the
          AlternativeStation cell; single-select over the client's Sample Sites. */}
      <StationPickerDialog
        open={stationPick != null}
        sites={config?.sampleSites ?? []}
        currentOid={samples.find(s => s.id === stationPick)?.v['AlternativeStationOid'] ?? ''}
        onPick={site => {
          const rowId = stationPick;
          if (rowId) {
            mutate(draft => {
              const s = draft.find(x => x.id === rowId);
              if (!s) return;
              s.v['AlternativeStation']    = site.name;
              s.v['AlternativeStationOid'] = site.oid;
            });
          }
          setStationPick(null);
        }}
        onClose={() => setStationPick(null)}
      />

      {/* Test popup (ALPACA "Test" row action) — opens the Test Assignment window
          for the clicked sample: the three G5 grids (Available Test · Selected Test
          · Selected Parameter) with Add / Remove / Save, the duplicate-parameter
          alert and the Reason popup. The inline panel's Edit button opens this SAME
          dialog (via onEdit → setTestPopup) for the marked sample. */}
      {testPopup && (() => {
        const one = samples.find(s => s.id === testPopup);
        if (!one) return null;
        return (
          <TestAssignmentDialog
            sample={one}
            matrixName={matrixMap?.[one.v.SampleMatrix ?? ''] ?? one.v.SampleMatrix ?? ''}
            testRows={testRows}
            groupChildren={groupChildren}
            jobOid={jobOid}
            justifiedParams={justifiedParams}
            onClose={() => setTestPopup(null)}
            onWarn={onWarn}
            onSave={(nextTests, justifications) => {
              mutate(draft => {
                const s = draft.find(x => x.id === one.id);
                if (!s) return;
                // Per-sample replace (G5 SampleParameter is per sample) — the popup
                // owns the full test set for this one sample.
                s.tests = nextTests.map(copyTest);
                autoBottles(s);
              });
              for (const t of nextTests) void fetchTestParams(t);
              if (justifications.length > 0) recordDuplicateJustifications?.(one.id, justifications);
              setTestPopup(null);
              // The popup only STAGES the test set in memory; it is persisted to the DB
              // solely by the outer Save / Save and Close (AI_SampleRegistration_SaveChildren).
              // Mark the form dirty so leaving WITHOUT saving triggers the shared
              // "Unsaved changes will be lost" guard (standing rule 15) instead of
              // silently dropping the tests — the samples live in the parent and change
              // via a programmatic mutate the native-input tracker cannot see, so we
              // announce it from a mounted form-root element. No "Tests updated…" info
              // banner: the unsaved-changes guard on navigation is the only prompt the
              // user wants (user request 2026-10-10) — the extra toast was redundant.
              markUserEdit(scrollRef.current);
            }}
          />
        );
      })()}

      {/* Cont popup (ALPACA "Containers" row action) — the G5 two-grid Containers
          window for the whole job: left = SampleID · Qty, right = per-test bottle
          allocation, with Assign / Copy / Reset Bottles and Save / Cancel changes.
          The clicked sample is pre-selected. */}
      {contPopup && (
        <ContainersPopup
          samples={samples}
          clickedId={contPopup}
          setSamples={setSamples}
          onClose={() => { setContPopup(null); setContTestKey(null); }}
          onSuccess={onSuccess}
          onWarn={onWarn}
          jobId={jobId}
          primaryMatrixOid={primaryMatrixOid}
          initialTestKey={contTestKey}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Test Assignment — the current sample's tests (BTLIMS grid design)
// ─────────────────────────────────────────────────────────────────────────────
function TestAssignment({ onOpenContainers, samples, setSamples, active, onSuccess, unitOptions, onEdit }: {
  jobId: string; samples: SrSample[]; setSamples: (n: SrSample[]) => void; active: number;
  onSuccess: (m: string) => void;
  unitOptions?: string[];
  /** Open the shared Test Assignment popup for the active sample (replaces the old
   *  inline "+ Add" — G5 parity, the inline grid's Edit opens the popup). */
  onEdit?: () => void;
  /** BottleID cell click → open the Cont (Containers) popup focused on that test. */
  onOpenContainers?: (testKey: string) => void;
}) {
  const sample = samples[active];
  const [focus, setFocus] = useState(0);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [paramFor, setParamFor] = useState<string | null>(null);   // test key
  const mutate = useCallback((fn: (s: SrSample, draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    const target = draft[active];
    if (!target) return;
    fn(target, draft);
    // Container / preservative / storage are PER-TEST (ALPACA parity): two tests may share
    // a BottleID yet keep different containers/preservatives, so they are never normalised
    // onto one shared value.
    setSamples(draft);
    markSamplesEdited();
  }, [samples, active, setSamples]);

  const [, setPV] = useState(0);
  const testKeys = sample?.tests.map(t => paramCacheKey(t)).join(',') ?? '';
  useEffect(() => {
    if (!sample) return;
    let cancel = false;
    const uncached = sample.tests.filter(t => !cachedParams(t));
    if (uncached.length > 0) {
      Promise.all(uncached.map(fetchTestParams)).then(() => { if (!cancel) setPV(v => v + 1); });
    }
    return () => { cancel = true; };
  }, [testKeys]);

  useEffect(() => {
    if (sample && focus >= sample.tests.length) setFocus(Math.max(0, sample.tests.length - 1));
  }, [sample, focus]);

  if (!sample) return null;

  const cols: AppColumn<SrTest>[] = [
    { field: 'name', header: 'Test', width: 170, render: t => <span style={{ fontWeight: 600 }}>{t.name}</span> },
    { field: 'testGroupName', header: 'TestGroupName', width: 150, sortable: true, filterable: true,
      render: t => t.testGroupName || '' },
    {
      field: 'bottle', header: 'BottleID', width: 90, sortable: false,
      render: t => (
        // Same combo control as the Cont popup's Bottle Assignment BottleID cell.
        <button type="button" className="sr-ba-combo" aria-label={`Bottle ID for ${t.name}`}
          onClick={e => { e.stopPropagation(); onOpenContainers?.(t.key); }}>
          <span>{t.bottle || '—'}</span><i className="ti ti-chevron-down" />
        </button>
      ),
      text: t => t.bottle,
    },
    { field: 'method', header: 'Method', width: 120 },
    { field: 'comp', header: 'Component', width: 110 },
    {
      field: 'params', header: 'Parameter', width: 120, sortable: false,
      render: t => (
        <button className="sr-edit-cell sr-edit-cell--text" style={{ color: 'var(--ai-pill-info-fg)', fontWeight: 600 }}
          onClick={e => { e.stopPropagation(); setParamFor(t.key); }}>
          {paramLabel(t)} ▾
        </button>
      ),
      text: t => paramLabel(t),
    },
    { field: 'storage', header: 'Storage', width: 100 },
    { field: 'hdTime', header: 'HoldTime', width: 110 },
    {
      field: 'status', header: 'Status', width: 140,
      edit: { type: 'combo', options: SAMPLE_STATUS_OPTS },
    },
    {
      field: 'remark', header: 'Remark', width: 70, align: 'center', sortable: false, filterable: false,
      render: t => (
        <input type="checkbox" checked={t.remark} aria-label={`Remark for ${t.name}`}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            const checked = e.target.checked;
            mutate(s => { const x = s.tests.find(y => y.key === t.key); if (x) x.remark = checked; });
          }} />
      ),
    },
    {
      field: 'subOut', header: 'SubOut', width: 70, align: 'center', sortable: false, filterable: false,
      render: t => (
        <input type="checkbox" checked={t.subOut} disabled
          aria-label={`Sub out ${t.name}`}
          title="Driven by Status — set status to 'Sub Out' to activate"
          onClick={e => e.stopPropagation()} />
      ),
    },
    { field: 'added', header: 'TestAddedDate', width: 130, type: 'date' },
  ];

  const paramTest = paramFor ? sample.tests.find(t => t.key === paramFor) : null;

  return (
    <section className="sr-sec">
      <h3 className="sr-sec-h">
        Test Assignment - <span style={{ color: 'var(--brand-accent-hover, var(--ai-orange-hover))' }}>{sample.id}</span>
        <span className="sr-sec-note">
          mark a Samples row above to switch sample · Edit opens the Test Assignment window · Parameter opens the analyte checklist
        </span>
      </h3>
      <div className="sr-sec-b">
        <div className="sr-subtoolbar" style={{ paddingTop: 0 }}>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="ca-btn" onClick={() => onEdit?.()}>
              <i className="ti ti-edit" /> Edit
            </button>
            <button className="ca-btn" disabled={sel.size === 0}
              onClick={() => {
                mutate((_, draft) => {
                  // Remove from ALL samples — tests are job-level in ALPACA,
                  // so deleting a test must remove it from every sample row
                  // to prevent it being re-inserted from a non-active sample on save.
                  for (const sample of draft) {
                    sample.tests = sample.tests.filter(t => !sel.has(t.key));
                    autoBottles(sample);
                  }
                });
                setSel(new Set());
                onSuccess(`${sel.size} test(s) removed.`);
              }}>
              <i className="ti ti-trash" style={{ color: 'var(--ai-icon-danger, #c0392b)' }} /> Delete
            </button>
          </span>
        </div>
        <AppDataGrid<SrTest>
          rows={sample.tests}
          columns={cols}
          getRowId={t => t.key}
          selectable selectedIds={sel} onSelectionChange={setSel}
          autoFocusFirstRow={false}
          onRowClick={t => setFocus(sample.tests.findIndex(x => x.key === t.key))}
          onCellCommit={(row, col, value) => {
            mutate(s => {
              const x = s.tests.find(y => y.key === row.key);
              if (x && col.field === 'status') {
                x.status = value;
                x.subOut = value === 'Sub Out';
              }
            });
          }}
          defaultPageSize={20}
          emptyText="No tests assigned yet — click Edit."
        />
      </div>

      {paramTest && (
        <ParameterPickerDialog
          test={paramTest}
          unitOptions={unitOptions}
          onClose={() => setParamFor(null)}
          onApply={(params, paramData) => {
            mutate(s => {
              const x = s.tests.find(y => y.key === paramTest.key);
              if (x) { x.params = params; x.paramData = paramData; }
            });
            setParamFor(null);
          }}
        />
      )}
    </section>
  );
}


/**
 * Test Assignment window — the three G5 grids (Available Test · Selected Test ·
 * Selected Parameter) with Add / Remove / Save, the duplicate-parameter alert and
 * the Reason popup. A faithful port of ALPACA's "Test Assignment - {SampleID}"
 * dashboard (SampleRegistrationViewController.Test_Execute).
 *
 * Opened from the Sample Login "Test" row link and the inline Test Assignment
 * panel's Edit button. Operates on a LOCAL working copy of the one sample's tests
 * (G5 SampleParameter is per sample). Save applies the working set to the form in
 * memory (onSave) — the DB write happens on the page-level Save (CLAUDE.md §1);
 * the duplicate-justification reason is audited there.
 */
export function TestAssignmentDialog({ sample, matrixName, testRows, groupChildren, jobOid, justifiedParams, onClose, onWarn, onSave }: {
  /** Saved job's Oid — lets Remove ask the server which tests are protected. */
  jobOid?: string | null;
  sample: SrSample;
  matrixName: string;
  testRows: string[][];
  groupChildren: Record<string, { name: string; method: string; comp: string; isFieldTest?: boolean; oid?: string }[]>;
  /** Parameter names already justified for this job — excluded from the duplicate
   *  check so the alert fires only ONCE per parameter (ALPACA parity). */
  justifiedParams?: Set<string>;
  onClose: () => void;
  onWarn: (m: string) => void;
  onSave: (nextTests: SrTest[], justifications: { paramName: string; testNames: string[]; reason: string }[]) => void;
}) {
  const { confirm } = useConfirm();
  // Working copy of this sample's tests (G5 per-sample SampleParameter set).
  const [working, setWorking] = useState<SrTest[]>(() => sample.tests.map(copyTest));
  // Snapshot of the tests present when the window OPENED (the saved / loaded set).
  // Duplicates already present here are "already accepted" and are never re-prompted
  // on Save — only a duplicate the user NEWLY introduces this session is flagged
  // (ALPACA ValidateDuplicateAnalysis grandfathers already-saved/justified rows).
  const baselineTests = useRef<SrTest[]>(sample.tests.map(copyTest)).current;
  const [availSel, setAvailSel] = useState<Set<string | number>>(new Set());
  const [selSel, setSelSel] = useState<Set<string | number>>(new Set());
  const [availFilter, setAvailFilter] = useState('');
  const [selFilter, setSelFilter] = useState('');
  const [paramFilter, setParamFilter] = useState('');
  const [pVer, setPVer] = useState(0);
  // Reason popup: holds the pending duplicate set awaiting justification.
  const [reason, setReason] = useState('');
  const [pendingDupes, setPendingDupes] = useState<{ paramName: string; testNames: string[] }[] | null>(null);

  // Load analyte caches for every working test (needed for the Parameter grid and
  // the duplicate check), re-rendering as they fill.
  const workKeys = working.map(t => `${t.name}|${t.method}|${t.comp || 'Default'}`).join(',');
  useEffect(() => {
    let cancel = false;
    const uncached = working.filter(t => !cachedParams(t));
    if (uncached.length > 0) {
      Promise.all(uncached.map(fetchTestParams)).then(() => { if (!cancel) setPVer(v => v + 1); });
    }
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workKeys]);

  // ── Available Test (testRows minus the working selection) ───────────────────
  type AvailRow = { id: number; test: string; method: string; isGroup: boolean; component: string };
  const workingKeys = useMemo(() => new Set(working.map(t => `${t.name}|${t.comp || 'Default'}`)), [working]);
  const availAll = useMemo<AvailRow[]>(() => testRows
    .map((r, i): AvailRow => ({ id: i, test: r[0] ?? '', method: r[1] ?? '', isGroup: r[2] === '1', component: r[3] ?? '' }))
    .filter(row => !workingKeys.has(`${row.test}|${row.component || 'Default'}`)),
    [testRows, workingKeys]);
  const availShown = useMemo(() => {
    const q = availFilter.trim().toLowerCase();
    if (!q) return availAll;
    return availAll.filter(r => `${r.test} ${r.method} ${r.component}`.toLowerCase().includes(q));
  }, [availAll, availFilter]);
  const availCols: AppColumn<AvailRow>[] = [
    { field: 'test', header: 'Test', width: 170 },
    { field: 'isGroup', header: 'IsGroup', width: 70, align: 'center', sortable: false,
      render: r => <input type="checkbox" checked={r.isGroup} readOnly style={{ pointerEvents: 'none' }} aria-label="Group test" />,
      text: r => (r.isGroup ? 'Yes' : '') },
    { field: 'method', header: 'Method', width: 110 },
    { field: 'component', header: 'Component', width: 110 },
  ];

  // ── Selected Test (the working set) ─────────────────────────────────────────
  const selShown = useMemo(() => {
    const q = selFilter.trim().toLowerCase();
    if (!q) return working;
    return working.filter(t => `${t.name} ${t.method} ${t.comp} ${t.testGroupName}`.toLowerCase().includes(q));
  }, [working, selFilter]);
  const selCols: AppColumn<SrTest>[] = [
    { field: 'name', header: 'Test', width: 150, render: t => <span style={{ fontWeight: 600 }}>{t.name}</span> },
    { field: 'method', header: 'Method', width: 110 },
    { field: 'comp', header: 'Component', width: 100 },
    { field: 'status', header: 'Status', width: 130, edit: { type: 'combo', options: SAMPLE_STATUS_OPTS } },
    { field: 'hdTime', header: 'HoldTime', width: 100 },
    { field: 'testGroupName', header: 'TestGroupName', width: 130, render: t => t.testGroupName || '' },
  ];

  // ── Selected Parameter (analytes of the CHECKED Selected rows) ──────────────
  // G5 parity: the grid lists EVERY analyte of the checked tests with a leading,
  // EDITABLE checkbox so the user can customize (exclude / re-include) which
  // parameters the test runs — the same per-test customization the Parameter
  // picker offers. A ticked row = included; unticking sets params[name]=false on
  // that test's working copy via toggleParam (a plain per-row checkbox — NOT the
  // grid's row-selection mechanism — so the click toggles directly and reliably).
  type ParamRow = { key: string; testKey: string; test: string; method: string; parameter: string; included: boolean };
  const checkedTests = useMemo(() => working.filter(t => selSel.has(t.key)), [working, selSel]);
  const paramAll = useMemo<ParamRow[]>(() => {
    const out: ParamRow[] = [];
    for (const t of checkedTests) {
      const names = cachedParams(t);
      if (!names) continue;
      // Show EVERY analyte (not just the included ones) so the checkbox can
      // re-enable one the user excluded — otherwise an unticked row would vanish.
      for (const p of names) {
        const included = !t.params || t.params[p] !== false;
        out.push({ key: `${t.key}|${p}`, testKey: t.key, test: t.name, method: t.method, parameter: p, included });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedTests, pVer]);
  const paramShown = useMemo(() => {
    const q = paramFilter.trim().toLowerCase();
    const base = q
      ? paramAll.filter(r => `${r.test} ${r.method} ${r.parameter}`.toLowerCase().includes(q))
      : paramAll;
    // Selected (ticked) parameters float to the TOP — ALPACA parity (user request
    // 2026-10-06): the legacy DevExpress Selected Parameter grid sorts checked rows
    // above unchecked. Array.sort is stable, so each group keeps its Test-Guide order.
    return [...base].sort((a, b) => (a.included === b.included ? 0 : a.included ? -1 : 1));
  }, [paramAll, paramFilter]);

  // Toggle one analyte's inclusion on its test's working copy. A test must keep
  // at least ONE analyte (G5 ValidateParameter / the Parameter picker both
  // enforce this), so unticking the last included one is a no-op.
  const toggleParam = (row: ParamRow) => {
    setWorking(prev => prev.map(t => {
      if (t.key !== row.testKey) return t;
      const names = cachedParams(t);
      if (!names || names.length === 0) return t;
      const map: Record<string, boolean> = {};
      for (const n of names) map[n] = t.params ? t.params[n] !== false : true;
      map[row.parameter] = !row.included;                     // flip the clicked one
      if (!names.some(n => map[n] !== false)) return t;        // never clear the last analyte
      const copy = copyTest(t);
      copy.params = map;
      return copy;
    }));
  };

  // Header select-all (like the Available / Selected Test grids): include / exclude
  // EVERY shown parameter at once. Unchecking the header clears every shown row even
  // to zero — a true check-all / uncheck-all, matching ALPACA (user request
  // 2026-10-06: "if I uncheck in the header all the rows should be unchecked"). The
  // "≥ 1 analyte per test" rule (G5 ValidateParameter) is enforced on Save instead of
  // by force-keeping a row ticked here. Operates on the filtered rows (paramShown).
  const allShownIncluded = paramShown.length > 0 && paramShown.every(r => r.included);
  const someShownIncluded = paramShown.some(r => r.included);
  const setAllShownParams = (include: boolean) => {
    const shownByTest = new Map<string, Set<string>>();
    for (const r of paramShown) {
      let s = shownByTest.get(r.testKey);
      if (!s) { s = new Set(); shownByTest.set(r.testKey, s); }
      s.add(r.parameter);
    }
    setWorking(prev => prev.map(t => {
      const shown = shownByTest.get(t.key);
      if (!shown || shown.size === 0) return t;
      const names = cachedParams(t);
      if (!names || names.length === 0) return t;
      const map: Record<string, boolean> = {};
      for (const n of names) map[n] = t.params ? t.params[n] !== false : true; // keep current
      for (const n of shown) map[n] = include;                                 // apply to shown (even to 0)
      const copy = copyTest(t);
      copy.params = map;
      return copy;
    }));
  };

  const paramCols: AppColumn<ParamRow>[] = [
    { field: '_inc', width: 42, align: 'center', sortable: false, filterable: false, noExport: true,
      headerText: '',
      header: (
        <input type="checkbox" className="sr-ta3-selall"
          ref={el => { if (el) el.indeterminate = someShownIncluded && !allShownIncluded; }}
          checked={allShownIncluded} disabled={paramShown.length === 0}
          aria-label="Include all parameters"
          onClick={e => e.stopPropagation()}
          onChange={() => setAllShownParams(!allShownIncluded)} />
      ),
      render: r => (
        <input type="checkbox" checked={r.included} aria-label={`Include ${r.parameter}`}
          onChange={() => toggleParam(r)} />
      ) },
    { field: 'test', header: 'Test', width: 150 },
    { field: 'method', header: 'Method', width: 110 },
    { field: 'parameter', header: 'Parameter', width: 150 },
  ];

  // ── Add / Remove ────────────────────────────────────────────────────────────
  const addSelected = () => {
    if (availSel.size === 0) { onWarn('Tick at least one test to add.'); return; }
    const next = working.map(copyTest);
    const has = (name: string, comp: string) => next.some(t => t.name === name && t.comp === (comp || 'Default'));
    const toCache: { name: string; method: string; comp: string }[] = [];
    const entered = sample.v.EnteredDate ?? '';
    for (const id of availSel) {
      const r = testRows[Number(id)];
      if (!r) continue;
      const name = r[0] ?? '';
      const method = r[1] ?? '';
      const isGroup = r[2] === '1';
      const component = r[3] ?? '';
      const fieldTest = r[4] === '1';
      const oid = r[5] || undefined;
      if (!name) continue;
      if (isGroup) {
        // Expand a group test into its child tests (GroupTestMethod), G5 parity.
        for (const ch of defaultComponentChildren(groupChildren[name] ?? [])) {
          const chComp = ch.comp || 'Default';
          const existing = next.find(t => t.name === ch.name && (t.comp || 'Default') === chComp);
          if (existing) {
            // Member already assigned — tag it with this group so its Test Group Name
            // shows too (user report: a pre-existing member linked to a group that is
            // now added must display the group name).
            existing.testGroupName = name;
            continue;
          }
          next.push(mkTest(ch.name, ch.method, ch.comp || undefined, entered, name, ch.isFieldTest, ch.oid || undefined));
          toCache.push({ name: ch.name, method: ch.method, comp: chComp });
        }
      } else {
        const comp = component || 'Default';
        if (has(name, comp)) continue;
        next.push(mkTest(name, method, comp, entered, '', fieldTest, oid));
        toCache.push({ name, method, comp });
      }
    }
    setWorking(next);
    setAvailSel(new Set());
    for (const c of toCache) fetchTestParams(c).then(() => setPVer(v => v + 1));
  };
  const removeSelected = async () => {
    if (selSel.size === 0) { onWarn('Tick at least one test to remove.'); return; }
    // Only tests this sample already HAS are protected; a test added in this popup but
    // not yet saved has nothing behind it.
    const blocked = await testDeleteBlockMessage(jobOid,
      sample.tests.filter(t => selSel.has(t.key)).map(t => ({ sampleId: sample.id, test: t })));
    if (blocked) { onWarn(blocked); return; }
    // Groups the removed tests belonged to — if removing a member leaves the group
    // incomplete, the remaining members must DROP the Test Group Name (user report:
    // removing one group member left the group name on the other members).
    const affectedGroups = new Set(
      working.filter(t => selSel.has(t.key) && t.testGroupName).map(t => t.testGroupName));
    const remaining = working.filter(t => !selSel.has(t.key)).map(copyTest);
    for (const g of affectedGroups) {
      const members = defaultComponentChildren(groupChildren[g] ?? []);
      const present = new Set(remaining.map(t => `${t.name}|${t.comp || 'Default'}`));
      const complete = members.length > 0
        && members.every(ch => present.has(`${ch.name}|${ch.comp || 'Default'}`));
      if (!complete) for (const t of remaining) if (t.testGroupName === g) t.testGroupName = '';
    }
    setWorking(remaining);
    setSelSel(new Set());
  };
  const commitStatus = (row: SrTest, value: string) => {
    setWorking(working.map(t => (t.key === row.key
      ? { ...copyTest(t), status: value, subOut: value === 'Sub Out' }
      : t)));
  };

  // ── Duplicate detection (G5 ValidateDuplicateAnalysis) ──────────────────────
  // A parameter appearing on MORE THAN ONE test ROW in `tests` is a duplicate
  // (counts rows, so two components of one test that both include the analyte are
  // caught); the message lists the DISTINCT test names it appears in.
  const dupesOf = (tests: SrTest[]): { paramName: string; testNames: string[] }[] => {
    const map = new Map<string, { names: Set<string>; count: number }>();
    for (const t of tests) {
      // ALPACA parity (user report 2026-10-10): a GROUP test is a lab-curated bundle
      // whose member tests intentionally cover overlapping analytes, so an analyte
      // shared across them is NOT a user "duplicate". Legacy's ValidateDuplicateAnalysis
      // runs ONLY over the header NPTest at the job Save — never in the per-sample Test
      // Assignment popup — so group-member overlaps never prompt there. Exclude group
      // members here so the prompt matches legacy; standalone duplicates still warn.
      if (t.testGroupName) continue;
      const names = cachedParams(t);
      if (!names) continue;
      const included = t.params ? names.filter(n => t.params![n] !== false) : names;
      for (const p of included) {
        let e = map.get(p);
        if (!e) { e = { names: new Set(), count: 0 }; map.set(p, e); }
        e.count += 1;
        e.names.add(t.name);
      }
    }
    const out: { paramName: string; testNames: string[] }[] = [];
    for (const [p, e] of map) if (e.count > 1) out.push({ paramName: p, testNames: [...e.names] });
    return out;
  };

  const doSave = async () => {
    // Ensure analytes are cached for BOTH the working set and the open-time baseline.
    await Promise.all([...working, ...baselineTests].map(fetchTestParams));
    // ALPACA ValidateParameter (G5): a test must keep ≥ 1 parameter. The header
    // select-all can now clear every parameter of a test (user request 2026-10-06),
    // so the ≥ 1 rule is enforced HERE instead of by force-keeping a row ticked —
    // a test with every analyte excluded blocks the save with the test name(s).
    const emptyTests = working.filter(t => {
      const names = cachedParams(t);
      if (!names || names.length === 0 || !t.params) return false; // no params / AllParam
      return !names.some(n => t.params![n] !== false);
    });
    if (emptyTests.length > 0) {
      onWarn(`Select at least one parameter for ${emptyTests.map(t => t.name).join(', ')}.`);
      return;
    }
    // ALPACA parity: grandfather duplicates that ALREADY existed when the window
    // opened (the saved / loaded set). ValidateDuplicateAnalysis does not re-prompt
    // for a duplicate that was accepted at its first save — so we only prompt for a
    // duplicate the user NEWLY introduced this session. (Params already justified
    // for the job via the audit trail are filtered too.)
    const baselineParams = new Set(dupesOf(baselineTests).map(d => d.paramName));
    const dupes = dupesOf(working).filter(d =>
      !baselineParams.has(d.paramName) && !(justifiedParams?.has(d.paramName) ?? false));
    if (dupes.length === 0) { onSave(working, []); return; }
    const msg = 'Duplicate parameters found:\n\n'
      + dupes.map(d => `• ${d.paramName} in [${d.testNames.join(', ')}]`).join('\n')
      + '\n\nDo you want to proceed?';
    const ok = await confirm({ title: 'Duplicate Parameters Found', message: msg, tone: 'danger' });
    if (!ok) return;
    // Proceed → capture a justification reason (G5 Reason popup); the save runs
    // when the reason is submitted.
    setReason('');
    setPendingDupes(dupes);
  };

  const submitReason = () => {
    if (!reason.trim()) { onWarn('Please enter the reason to justify the duplicate parameters.'); return; }
    const justifications = (pendingDupes ?? []).map(d => ({
      paramName: d.paramName, testNames: d.testNames, reason: reason.trim(),
    }));
    setPendingDupes(null);
    onSave(working, justifications);
  };

  return (
    <AppDialog open icon="ti-flask" width={1560} maximizable submitOnEnter={false}
      title={`Test Assignment - ${sample.id}`} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={addSelected}><i className="ti ti-arrow-right" /> Add</button>
        <button className="ca-btn" onClick={removeSelected}><i className="ti ti-arrow-left" /> Remove</button>
        <button className="ca-btn ca-btn-solid" onClick={() => void doSave()}>Save</button>
      </>}>
      <div style={{ marginBottom: 8, fontSize: 13 }}>
        <b>Matrix:</b> {matrixName || '—'}
      </div>
      <div className="sr-ta3">
        <div className="sr-ta3-col">
          <div className="sr-ta3-h">Available Test</div>
          <div className="sr-pick-f" style={{ border: '1px solid var(--ai-line)', borderRadius: 5, marginBottom: 6 }}>
            <input placeholder="Enter text to search…" value={availFilter} onChange={e => setAvailFilter(e.target.value)} />
          </div>
          <div className="sr-ta3-grid">
            <AppDataGrid<AvailRow> rows={availShown} columns={availCols} getRowId={r => r.id}
              selectable selectedIds={availSel} onSelectionChange={setAvailSel}
              fixedLayout wrapHeaders
              defaultPageSize={50} emptyText="No matches." />
          </div>
        </div>
        <div className="sr-ta3-col sr-ta3-col--wide">
          <div className="sr-ta3-h">Selected Test</div>
          <div className="sr-pick-f" style={{ border: '1px solid var(--ai-line)', borderRadius: 5, marginBottom: 6 }}>
            <input placeholder="Enter text to search…" value={selFilter} onChange={e => setSelFilter(e.target.value)} />
          </div>
          <div className="sr-ta3-grid">
            <AppDataGrid<SrTest> rows={selShown} columns={selCols} getRowId={t => t.key}
              selectable selectedIds={selSel} onSelectionChange={setSelSel}
              fixedLayout wrapHeaders
              onCellCommit={(row, col, value) => { if (col.field === 'status') commitStatus(row, value); }}
              defaultPageSize={50} emptyText="No tests selected." />
          </div>
        </div>
        <div className="sr-ta3-col">
          <div className="sr-ta3-h">Selected Parameter</div>
          <div className="sr-pick-f" style={{ border: '1px solid var(--ai-line)', borderRadius: 5, marginBottom: 6 }}>
            <input placeholder="Enter text to search…" value={paramFilter} onChange={e => setParamFilter(e.target.value)} />
          </div>
          <div className="sr-ta3-grid">
            <AppDataGrid<ParamRow> rows={paramShown} columns={paramCols} getRowId={r => r.key}
              fixedLayout wrapHeaders
              defaultPageSize={200} emptyText="Select a test to view its parameters." />
          </div>
        </div>
      </div>

      {pendingDupes && (
        <AppDialog open icon="ti-message" width={520} title="Reason" submitOnEnter={false}
          onClose={() => setPendingDupes(null)}
          footer={<>
            <button className="ca-btn" onClick={() => setPendingDupes(null)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={submitReason}>OK</button>
          </>}>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Reason:</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={5} autoFocus
            aria-label="Reason for duplicate parameters"
            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--ai-line)', borderRadius: 4, padding: 8, fontSize: 13, resize: 'vertical' }} />
        </AppDialog>
      )}
    </AppDialog>
  );
}

/** ParamList checklist — "n of total" + the field-method result columns.
 *  Parameters are loaded from dbo.Testparameter via the API (QCType=Sample,
 *  filtered by TestMethod + Component, excluding surrogates/internal standards). */
function ParameterPickerDialog({ test, onClose, onApply, unitOptions }: {
  test: SrTest; onClose: () => void;
  onApply: (params: Record<string, boolean>, paramData: Record<string, SrParamData>) => void;
  unitOptions?: string[];
}) {
  const field = isFieldTest(test);
  const [list, setList] = useState<string[]>(() => cachedParams(test) ?? []);
  const [loading, setLoading] = useState(!cachedParams(test));
  const [params, setParams] = useState<Record<string, boolean>>(() => test.params ?? {});
  const [data, setData] = useState<Record<string, SrParamData>>(() => ({ ...test.paramData }));

  useEffect(() => {
    let cancel = false;
    fetchTestParams(test).then(names => {
      if (cancel) return;
      setList(names);
      setLoading(false);
      // Bug 16: a FIELD test pre-fills each parameter's Units from the test code
      // setup (Testparameter.DefaultUnits) — the analyst never picks it by hand. A
      // value already entered/saved is never overwritten.
      if (isFieldTest(test)) {
        setData(d => {
          let next = d;
          for (const pm of names) {
            const u = cachedParamUnit(test, pm);
            if (!u || d[pm]?.units) continue;
            if (next === d) next = { ...d };
            next[pm] = { result: '', analysisDate: '', analyzedBy: '', ...(d[pm] ?? {}), units: u };
          }
          return next;
        });
      }
      setParams(prev => {
        if (Object.keys(prev).length > 0) return prev;
        return Object.fromEntries(names.map(p => [p, true]));
      });
    });
    return () => { cancel = true; };
  }, [test.name, test.method, test.comp]);

  const on = list.filter(p => params[p] !== false).length;

  const setField = (pm: string, k: keyof SrParamData, v: string) =>
    setData(d => ({
      ...d,
      [pm]: { result: '', units: '', analysisDate: '', analyzedBy: '', ...(d[pm] ?? {}), [k]: v },
    }));

  return (
    <AppDialog open title={`${test.name} — parameters`} icon="ti-list-check"
      width={field ? 800 : 420} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={loading || on < 1}
          onClick={() => onApply(params, data)}>Apply</button>
      </>}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--ai-muted)' }}>
          Loading parameters…
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>{on} of {list.length}</span>
            <span className="sr-hint-inline">
              {field
                ? 'Field method — record on-site results below'
                : 'Lab method — results are entered in Result Entry'}
            </span>
          </div>
          <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
            <table className="sr-pick-table">
              <thead>
                <tr>
                  <th className="sr-pick-cb">
                    <input type="checkbox" checked={on === list.length}
                      aria-label="Select all parameters"
                      onChange={e => {
                        // Unchecking clears EVERY row; Apply stays disabled until one is ticked.
                        setParams(Object.fromEntries(list.map(p => [p, e.target.checked])));
                      }} />
                  </th>
                  <th>Parameter</th>
                  {field && <>
                    <th style={{ width: 96 }}>Result</th>
                    <th style={{ width: 104 }}>Units</th>
                    <th style={{ width: 180 }}>AnalysisDate</th>
                    <th style={{ width: 120 }}>AnalyzedBy</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {list.map(pm => {
                  const d = data[pm];
                  return (
                    <tr key={pm}>
                      <td className="sr-pick-cb">
                        <input type="checkbox" checked={params[pm] !== false} aria-label={pm}
                          onChange={e => {
                            setParams(p => ({ ...p, [pm]: e.target.checked }));
                          }} />
                      </td>
                      <td>{pm}</td>
                      {field && <>
                        <td><input value={d?.result ?? ''} aria-label={`${pm} result`}
                          onChange={e => setField(pm, 'result', e.target.value)} style={CELL_INPUT} /></td>
                        <td>{unitOptions?.length ? (
                          <select value={d?.units ?? ''} aria-label={`${pm} units`}
                            onChange={e => setField(pm, 'units', e.target.value)} style={CELL_INPUT}
                            className="sr-unit-sel">
                            <option value="">N/A</option>
                            {[...new Set([d?.units, ...unitOptions].filter((u): u is string => !!u))]
                              .map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        ) : (
                          <input value={d?.units ?? ''} aria-label={`${pm} units`}
                            onChange={e => setField(pm, 'units', e.target.value)} style={CELL_INPUT} />
                        )}</td>
                        <td><UsDateTimeInput mode="datetime" value={d?.analysisDate ?? ''}
                          className={`sr-param-dt${!(d?.analysisDate ?? '') ? ' sr-dt-empty' : ''}`}
                          onChange={v => setField(pm, 'analysisDate', v)} /></td>
                        <td><input value={d?.analyzedBy ?? ''} aria-label={`${pm} analyzed by`}
                          onChange={e => setField(pm, 'analyzedBy', e.target.value)} style={CELL_INPUT} /></td>
                      </>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AppDialog>
  );
}

const CELL_INPUT: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', border: '1px solid var(--ai-line)',
  borderRadius: 3, padding: '3px 6px', fontSize: 12,
};

/** BottleID editor — the sample's Qty drives the bottle list ({SampleID}-A…). */
function BottlePickerDialog({ sample, test, jobId, onClose, onApply, bl, filteredContainers, filteredPreservatives, guideContainer, guidePreserv }: {
  sample: SrSample; test: SrTest; jobId: string; onClose: () => void;
  onApply: (bottles: string[], bd: Record<string, import('./srData').SrBottleDetail>) => void;
  bl: BottleLookups;
  /** Optional pre-filtered container options for this test's method. Falls back to bl.containers. */
  filteredContainers?: BottleLookups['containers'];
  /** Optional pre-filtered preservative options for this test's method. Falls back to bl.preservatives. */
  filteredPreservatives?: BottleLookups['preservatives'];
  /** Top TestGuide default (guideFirstDefault): the FIRST distinct value in Test
   *  Guide order, undefined when the method has no guide rows. */
  guideContainer?: string;
  guidePreserv?: string;
}) {
  const containers  = filteredContainers?.length  ? filteredContainers  : bl.containers;
  const preservatives = filteredPreservatives?.length ? filteredPreservatives : bl.preservatives;
  const qty = Math.max(1, Number.parseInt(sample.v.Qty ?? '1', 10) || 1);
  const letters = Array.from({ length: qty }, (_, i) => String.fromCharCode(65 + i));
  const [picked, setPicked] = useState<Set<string>>(() => {
    const s = new Set((test.bottle || '').split(',').filter(Boolean));
    if (s.size === 0) s.add('A');
    return s;
  });
  const [bd, setBd] = useState<Record<string, import('./srData').SrBottleDetail>>(() => {
    // The TestGuide top default (guideContainer/guidePreserv) wins — the FIRST
    // distinct value in Test Guide order (owner decision 2026-09-30, so a test
    // with several guide rows still seeds the top one, not a blank). Only a method
    // with NO guide rows (undefined) falls back to the test row / per-test default.
    const d = testDefaults(test.name);
    const gc = guideContainer !== undefined ? guideContainer : (test.container || d.container);
    const gp = guidePreserv   !== undefined ? guidePreserv   : (test.preserv || d.preserv);
    const seed: Record<string, import('./srData').SrBottleDetail> = { ...(test.bd ?? {}) };
    for (const L of letters) {
      seed[L] ??= { container: gc, preserv: gp, storage: test.storage || '', storageCondition: '' };
    }
    return seed;
  });

  const set = (L: string, k: keyof import('./srData').SrBottleDetail, v: string) =>
    setBd(m => ({ ...m, [L]: { ...m[L]!, [k]: v } }));

  return (
    <AppDialog open width={1000} icon="ti-bottle"
      title={`${test.name} · bottles (Qty ${qty})`} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid"
          onClick={() => onApply([...picked].sort(), bd)}>Apply</button>
      </>}>
      <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
        <table className="sr-pick-table sr-bottlepick" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: '3%' }} />
              <th style={{ width: '16%' }}>Bottle ID</th>
              <th style={{ width: '22%' }}>Container</th>
              <th style={{ width: '17%' }}>Preservation</th>
              <th style={{ width: '22%' }}>Storage Condition</th>
              <th style={{ width: '20%' }}>Storage</th>
            </tr>
          </thead>
          <tbody>
            {letters.map(L => {
              const d = bd[L]!;
              const on = picked.has(L);
              return (
                <tr key={L} className={on ? 'on' : ''}>
                  <td className="sr-pick-cb">
                    <input type="checkbox" checked={on} aria-label={`Bottle ${L}`}
                      onChange={() => setPicked(p => {
                        const n = new Set(p);
                        if (n.has(L)) n.delete(L); else n.add(L);
                        return n;
                      })} />
                  </td>
                  <td style={{ fontWeight: 600 }}>{sample.id || `${jobId}-001`}-{L}</td>
                  <td>
                    <select value={d.container} aria-label={`Bottle ${L} container`}
                      onChange={e => set(L, 'container', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {containers.map(c => <option key={c.oid} value={c.name}>{c.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={d.preserv} aria-label={`Bottle ${L} preservation`}
                      onChange={e => set(L, 'preserv', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {preservatives.map(p => <option key={p.oid} value={p.name}>{p.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <ComboSelect value={d.storageCondition} placeholder="N/A"
                      options={bl.storageConditions.map(sc => sc.name)}
                      onChange={v => set(L, 'storageCondition', v)} />
                  </td>
                  <td>
                    <select value={d.storage} aria-label={`Bottle ${L} storage`}
                      onChange={e => set(L, 'storage', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {bl.storages.map(s => <option key={s.oid} value={s.name}>{s.name}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="sr-pick-g" style={{ border: 'none', background: 'none', paddingLeft: 0 }}>
        <span>Container &amp; Preservation are pre-filled from the test's defaults — edit to override.</span>
        <span>{picked.size} of {qty} selected</span>
      </div>
    </AppDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Test View tab — one row per sample × test
// ─────────────────────────────────────────────────────────────────────────────
interface FlatTest { key: string; sample: SrSample; test: SrTest }

function TestViewTab({ samples, setSamples, onSuccess, onWarn, confirmAction, matrixMap, unitOptions, jobOid }: SampleLoginViewProps) {
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [paramFor, setParamFor] = useState<{ sampleId: string; testKey: string } | null>(null);

  // Ensure paramLabel shows 'All Param' / 'Customized' (not '…') by fetching
  // any uncached test-parameter lists and triggering a re-render on completion.
  const [pVer, setPVer] = useState(_paramVer);
  useEffect(() => {
    let cancel = false;
    const unique = new Map<string, { name: string; method: string; comp: string }>();
    for (const s of samples) for (const t of s.tests) {
      const k = paramCacheKey(t);
      if (!_paramCache.has(k)) unique.set(k, t);
    }
    if (unique.size === 0) return;
    Promise.all([...unique.values()].map(fetchTestParams)).then(() => {
      if (!cancel) setPVer(_paramVer);
    });
    return () => { cancel = true; };
  }, [samples]);

  const rows: FlatTest[] = useMemo(
    () => samples.flatMap(s => s.tests.map(t => ({ key: `${s.id}|${t.key}`, sample: s, test: t }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [samples, pVer],
  );

  const mutate = (fn: (draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    fn(draft);
    setSamples(draft);
    markSamplesEdited();
  };

  const paramTest = paramFor
    ? samples.find(s => s.id === paramFor.sampleId)?.tests.find(t => t.key === paramFor.testKey) ?? null
    : null;

  const cols: AppColumn<FlatTest>[] = [
    { field: 'sampleId', header: 'SampleID', width: 150, text: r => r.sample.id,
      render: r => <span style={{ fontWeight: 600 }}>{r.sample.id}</span> },
    { field: 'matrix', header: 'Matrix', width: 120,
      text: r => matrixMap?.[(r.sample.v.SampleMatrix ?? '').toLowerCase()] ?? r.sample.v.SampleMatrix ?? '' },
    { field: 'test', header: 'Test', width: 220, text: r => r.test.name },
    { field: 'testGroupName', header: 'TestGroupName', width: 150, sortable: true, filterable: true,
      text: r => r.test.testGroupName, render: r => r.test.testGroupName || '' },
    { field: 'method', header: 'Method', width: 130, text: r => r.test.method },
    { field: 'comp', header: 'Component', width: 110, text: r => r.test.comp },
    {
      field: 'status', header: 'Status', width: 150,
      text:  r => r.test.status,
      value: r => r.test.status,
      edit: { type: 'combo', options: SAMPLE_STATUS_OPTS },
    },
    {
      field: 'param', header: 'Parameter', width: 130, sortable: false,
      render: r => (
        <button className="sr-edit-cell sr-edit-cell--text" style={{ color: 'var(--ai-pill-info-fg)', fontWeight: 600 }}
          onClick={e => { e.stopPropagation(); setParamFor({ sampleId: r.sample.id, testKey: r.test.key }); }}>
          {paramLabel(r.test)} ▾
        </button>
      ),
      text: r => paramLabel(r.test),
    },
  ];

  return (
    <>
      <div className="sr-subtoolbar">
        <span className="sr-sublbl">Tests — all tests per sample</span>
        <button className="ca-btn" disabled={sel.size === 0}
          onClick={() => confirmAction('Delete tests', `Delete ${sel.size} test assignment(s)?`, async () => {
            // GoLive delete guards (result entered / SDMS / QC batch / prep / sub-out):
            // a saved test that is in use stays — show the legacy message, delete nothing.
            const items = [...sel].flatMap(key => {
              const k = String(key);
              const p = k.indexOf('|');
              const sampleId = k.slice(0, p);
              const test = samples.find(x => x.id === sampleId)?.tests.find(t => t.key === k.slice(p + 1));
              return test ? [{ sampleId, test }] : [];
            });
            const blocked = await testDeleteBlockMessage(jobOid, items);
            if (blocked) { onWarn(blocked); return; }
            mutate(draft => {
              for (const key of sel) {
                // Row key is "${sampleId}|${t.key}" where t.key itself contains "|"
                // (format: "testMethodOid|component"). Split only on the FIRST pipe.
                const k = String(key);
                const firstPipe = k.indexOf('|');
                const sampleId = k.slice(0, firstPipe);
                const testKey  = k.slice(firstPipe + 1);
                const s = draft.find(x => x.id === sampleId);
                if (s) s.tests = s.tests.filter(t => t.key !== testKey);
              }
              draft.forEach(autoBottles);
            });
            setSel(new Set());
            onSuccess('Test(s) deleted.');
          }, true)}>
          <span className="tb-i tb-i-red">🗑</span> Delete
        </button>
        <ExportMenu filename="test-view" triggerClassName="ca-btn"
          headers={['SampleID', 'Matrix', 'Test', 'TestGroupName', 'Method', 'Component', 'Status', 'Parameter']}
          rows={rows.map(r => [r.sample.id, matrixMap?.[(r.sample.v.SampleMatrix ?? '').toLowerCase()] ?? '', r.test.name,
            r.test.testGroupName, r.test.method, r.test.comp, r.test.status, paramLabel(r.test)])} />
      </div>
      <AppDataGrid<FlatTest>
        rows={rows} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        onCellCommit={(row, col, value) => {
          if (col.field !== 'status') return;
          mutate(draft => {
            const t = draft.find(s => s.id === row.sample.id)?.tests.find(x => x.key === row.test.key);
            if (t) { t.status = value; t.subOut = value === 'Sub Out'; }
          });
        }}
        defaultPageSize={100}
        emptyText="No tests assigned yet."
      />
      {rows.length === 0 && <p className="sr-note">Assign tests in <b>Samples ▸ Test Assignment</b>.</p>}

      {paramTest && paramFor && (
        <ParameterPickerDialog
          test={paramTest}
          unitOptions={unitOptions}
          onClose={() => setParamFor(null)}
          onApply={(params, paramData) => {
            mutate(draft => {
              const s = draft.find(x => x.id === paramFor.sampleId);
              const x = s?.tests.find(y => y.key === paramFor.testKey);
              if (x) { x.params = params; x.paramData = paramData; }
            });
            setParamFor(null);
          }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Containers tab — By Sample (master-detail) / All in One (flat)
// ─────────────────────────────────────────────────────────────────────────────
/** The Containers tab needs only these seven props, so it is exported with a
 *  NARROWED type - Sample Transfer renders the very same component (standing
 *  rule 8) and has no business supplying Registration's tab strip, record
 *  actions or collector/matrix maps. */
export type ContainersTabProps = Pick<SampleLoginViewProps,
  'samples' | 'setSamples' | 'active' | 'setActive' | 'onSuccess' | 'onWarn' | 'jobId' | 'primaryMatrixOid'>
  & {
    /** G5 Containers popup: show ONLY the two-grid master-detail (left SampleID+Qty,
     *  right test bottles) with no All-in-One / By-Sample toggle. */
    hideViewToggle?: boolean;
    /** "Cont" link popup layout (user request 2026-10-05): scope the two-grid
     *  master-detail to the ACTIVE (clicked) sample — left pane lists only that
     *  sample's test names, right pane shows ONLY the focused test's bottle row.
     *  Replaces the SampleID-list ▸ all-tests layout. Set only by ContainersPopup;
     *  the wizard Containers tab leaves it off, so its By-Sample view is unchanged. */
    testMasterDetail?: boolean;
    /** Cont popup opened from a Test Assignment BottleID cell: pre-focus that test. */
    initialTestKey?: string | null;
  };

export function ContainersTab({ samples, setSamples, active, setActive, onSuccess, onWarn, jobId, primaryMatrixOid, hideViewToggle, testMasterDetail, initialTestKey }: ContainersTabProps) {
  // Containers sub-view defaults to "All in One" (flat, all bottles) — the primary
  // view; "By Sample" (master-detail) is secondary (user request 2026-09-23). The
  // G5 Containers popup forces the two-grid "By Sample" layout.
  const [view, setView] = useState<'bysample' | 'all'>(hideViewToggle ? 'bysample' : 'all');
  // The Cont popup (testMasterDetail) has its OWN two-tab toggle below (Containers /
  // Container Summary), so it honours `view` too; only the legacy hideViewToggle-
  // without-master-detail case is forced to the two-grid layout.
  const effectiveView: 'bysample' | 'all' = (hideViewToggle && !testMasterDetail) ? 'bysample' : view;
  const [bottleFor, setBottleFor] = useState<{ sampleId: string; testKey: string } | null>(null);
  // Cont-popup master-detail (testMasterDetail): the test row in focus on the left,
  // whose bottle row alone shows on the right. Null until the user clicks; the render
  // falls back to the active sample's first lab test.
  const [focusedTestKey, setFocusedTestKey] = useState<string | null>(initialTestKey ?? null);

  // Compute all unique testMethodOids from every sample's tests so the
  // bottle-lookups API can filter Container / Preservative via TestGuide.
  const testMethodOids = useMemo(() => {
    const ids = new Set<string>();
    for (const s of samples) {
      for (const t of s.tests) {
        if (t.testMethodOid) ids.add(t.testMethodOid);
      }
    }
    return [...ids].sort().join(',');
  }, [samples]);

  const bl = useBottleLookups(testMethodOids || undefined);

  // Build per-testMethodOid allowed container / preservative name sets from RS5.
  const tgContainers = useMemo(() => {
    const m = new GuidKeyMap<Set<string>>();
    for (const row of bl.testGuide) {
      if (!row.containerName) continue;
      let s = m.get(row.testMethodOid);
      if (!s) { s = new Set(); m.set(row.testMethodOid, s); }
      s.add(row.containerName);
    }
    return m;
  }, [bl.testGuide]);

  const tgPreservatives = useMemo(() => {
    const m = new GuidKeyMap<Set<string>>();
    for (const row of bl.testGuide) {
      if (!row.preservativeName) continue;
      let s = m.get(row.testMethodOid);
      if (!s) { s = new Set(); m.set(row.testMethodOid, s); }
      s.add(row.preservativeName);
    }
    return m;
  }, [bl.testGuide]);

  /** Filter container options to those allowed for this test's method. */
  const containersFor = (t: SrTest) => {
    if (!t.testMethodOid) return bl.containers;
    const allowed = tgContainers.get(t.testMethodOid);
    if (!allowed || allowed.size === 0) return bl.containers;
    return bl.containers.filter(c => allowed.has(c.name));
  };

  /** Filter preservative options to those allowed for this test's method. */
  const preservativesFor = (t: SrTest) => {
    if (!t.testMethodOid) return bl.preservatives;
    const allowed = tgPreservatives.get(t.testMethodOid);
    if (!allowed || allowed.size === 0) return bl.preservatives;
    return bl.preservatives.filter(p => allowed.has(p.name));
  };

  const mutate = (fn: (draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    fn(draft);
    setSamples(draft);
    markSamplesEdited();
  };

  // XAF behaviour: each test row tracks its OWN container / preservative /
  // storage independently — two tests on bottle "A" can have different
  // containers/preservatives (ALPACA parity), so they are NOT normalised together.
  const setTestField = (sampleId: string, testKey: string, field: 'container' | 'preserv' | 'storage' | 'storageCondition', value: string) => {
    mutate(draft => {
      const t = draft.find(x => x.id === sampleId)?.tests.find(x => x.key === testKey);
      if (!t) return;
      if (field === 'storageCondition') {
        // Storage Condition lives only per bottle (bd) — SrTest has no test-level field.
        // Create the first letter's detail when the test has none yet.
        if (!t.bd) t.bd = {};
        const first = (t.bottle || 'A').split(',')[0]?.trim() || 'A';
        if (!t.bd[first]) t.bd[first] = { container: t.container, preserv: t.preserv, storage: t.storage, storageCondition: '' };
        for (const letter of Object.keys(t.bd)) {
          const d = t.bd[letter];
          if (d) d.storageCondition = value;
        }
        return;
      }
      t[field] = value;
      // Picking a Container fills the Preservative its Test Guide row pairs it with.
      const pairedPreserv = field === 'container' ? guidePreservFor(bl.testGuide, t.testMethodOid, value) : undefined;
      if (pairedPreserv !== undefined) t.preserv = pairedPreserv;
      // Push the value down into EVERY bottle letter of this test. A "By Sample"
      // row IS the test (one row per test, covering all its bottles), so editing
      // it must move its bottles with it. Without this the per-bottle detail kept
      // the value it was seeded with — Assign Bottles seeds `storage: ''` — and
      // since BOTH the "All in One" grid and the SAVE payload read
      // `bd[letter].storage ?? t.storage`, an empty string is NOT null, so the
      // fallback never fired: the storage picked here showed as N/A in All in One
      // and was never persisted (user report 2026-10-03).
      if (t.bd) {
        for (const letter of Object.keys(t.bd)) {
          const d = t.bd[letter];
          if (d) { d[field] = value; if (pairedPreserv !== undefined) d.preserv = pairedPreserv; }
        }
      }
    });
  };

  // Updates an individual bottle letter's container/preserv/storage in bd, and
  // keeps the test-level field in sync for the first letter (used by "By Sample"
  // view and the save path which reads t.container / t.preserv / t.storage).
  const setBottleField = (sampleId: string, testKey: string, letter: string, field: 'container' | 'preserv' | 'storage' | 'storageCondition', value: string) => {
    mutate(draft => {
      const t = draft.find(x => x.id === sampleId)?.tests.find(x => x.key === testKey);
      if (!t) return;
      if (!t.bd) t.bd = {};
      if (!t.bd[letter]) t.bd[letter] = { container: t.container, preserv: t.preserv, storage: t.storage, storageCondition: '' };
      t.bd[letter]![field] = value;
      if (field === 'container') {
        const paired = guidePreservFor(bl.testGuide, t.testMethodOid, value);
        if (paired !== undefined) t.bd[letter]!.preserv = paired;
      }
      // Keep test-level in sync for the first bottle letter (storageCondition is bd-only).
      const firstLetter = (t.bottle || 'A').split(',')[0]?.trim() ?? 'A';
      if (field !== 'storageCondition' && letter === firstLetter) {
        t[field] = value;
        if (field === 'container') t.preserv = t.bd[letter]!.preserv;
      }
    });
  };

  // Replace a test's whole bottle set (letters + per-bottle details) — the editable
  // Bottle Assignment grid (testMasterDetail) commits through here. Qty is NOT
  // re-derived: the popup's "Container Qty" control owns the sample's bottle pool, so
  // adding / removing a bottle must not shrink that pool (unlike BottlePickerDialog,
  // which syncs Qty). The test-level container/preserv/storage mirror the first bottle.
  const updateTestBottles = (sampleId: string, testKey: string, letters: string[], bd: Record<string, SrBottleDetail>) => {
    mutate(draft => {
      const t = draft.find(x => x.id === sampleId)?.tests.find(x => x.key === testKey);
      if (!t) return;
      t.bottle = letters.join(',');
      t.bd = bd;
      const first = letters[0];
      if (first && bd[first]) {
        t.container = bd[first].container;
        t.preserv   = bd[first].preserv;
        t.storage   = bd[first].storage;
      }
    });
  };

  const [copyOpen, setCopyOpen] = useState(false);

  // Legacy refuses to open the popup with fewer than two samples.
  const openCopyBottles = () => {
    if (samples.length < 2) { onWarn("Add atleast two sampleID's"); return; }
    setCopyOpen(true);
  };

  const copyBottles = (fromId: string, toIds: string[]) => {
    const src = samples.find(s => s.id === fromId);
    if (!src) { onWarn('Select the from.'); return; }
    const toSet = new Set(toIds);
    let n = 0;
    mutate(draft => {
      const source = draft.find(s => s.id === fromId);
      if (!source) return;
      for (const s of draft) {
        if (s === source || !toSet.has(s.id)) continue;
        s.v.Qty = source.v.Qty ?? '1';
        // Carry the source sample's Qty ownership so the copied Qty behaves like the source's.
        if (qtyIsManual(source)) markQtyManual(s); else clearQtyManual(s);
        for (const t of s.tests) {
          const m = source.tests.find(x => x.name === t.name && x.comp === t.comp);
          if (!m) continue;
          t.bottle = m.bottle; t.container = m.container; t.preserv = m.preserv; t.storage = m.storage;
          t.bd = m.bd ? Object.fromEntries(Object.entries(m.bd).map(([k, v]) => [k, { ...v }])) : null;
        }
        n += 1;
      }
    });
    setCopyOpen(false);
    onSuccess(`Bottle setup copied from ${src.id} to ${n} sample(s).`);
  };

  const resetBottles = () => {
    const cur = samples[active];
    if (!cur) { onWarn('Select a sample.'); return; }
    mutate(draft => {
      const s = draft[active];
      if (!s) return;
      // Reset Bottles is a full reset (legacy btnResetbottles sets Qty back), so the
      // user-owned Qty marker is cleared and Qty re-derives from the reset defaults.
      clearQtyManual(s);
      s.v.Qty = '1';
      for (const t of s.tests) {
        const d = testDefaults(t.name);
        t.bottle = 'A'; t.container = d.container; t.preserv = d.preserv; t.storage = ''; t.bd = null;
      }
      autoBottles(s);
    });
    onSuccess(`Bottles reset to defaults for ${cur.id}.`);
  };

  // Assign Bottles — ALPACA btnAssignbottles_BottleAllocation_Execute: honour the
  // per-matrix BottleSharing rules (tests that share a bottle get the same letter),
  // then give every other lab test its own sequential letter; Qty = bottle count.
  const assignBottles = async () => {
    const cur = samples[active];
    if (!cur) { onWarn('Select a sample.'); return; }
    const matrixOid = sampleMatrixOid(cur, primaryMatrixOid);
    const groups = matrixOid ? await fetchBottleSharing(matrixOid) : [];
    mutate(draft => {
      const s = draft[active];
      if (!s) return;
      assignBottleLetters(s, groups);
    });
    onSuccess(`Bottle assigned successfully for ${cur.id}.`);
  };

  const bottleCols = (sampleId: string, _sample: SrSample): AppColumn<SrTest>[] => [
    { field: 'name', header: 'Test', width: 160, render: t => <span style={{ fontWeight: 600 }}>{t.name}</span> },
    {
      field: 'bottle', header: 'BottleID', width: 110, sortable: false,
      text: t => t.bottle || 'A',
      render: t => (
        <button
          className="sr-bottle-btn"
          title="Click to assign bottles"
          onClick={() => setBottleFor({ sampleId, testKey: t.key })}
          style={{ ...CELL_INPUT, cursor: 'pointer', textAlign: 'left', background: 'var(--ai-surface)', border: '1px solid var(--ai-line)' }}>
          {t.bottle || 'A'}
        </button>
      ),
    },
    { field: 'method', header: 'Method', width: 120 },
    {
      field: 'container', header: 'Containers', width: 170, sortable: false,
      render: t => (
        <select value={t.container} aria-label={`${t.name} container`} style={CELL_INPUT}
          onChange={e => setTestField(sampleId, t.key, 'container', e.target.value)}>
          <option value="">N/A</option>
          {containersFor(t).map(c => <option key={c.oid} value={c.name}>{c.name}</option>)}
        </select>
      ),
      text: t => t.container,
    },
    {
      field: 'preserv', header: 'Preservative', width: 140, sortable: false,
      render: t => (
        <select value={t.preserv} aria-label={`${t.name} preservative`} style={CELL_INPUT}
          onChange={e => setTestField(sampleId, t.key, 'preserv', e.target.value)}>
          <option value="">N/A</option>
          {preservativesFor(t).map(p => <option key={p.oid} value={p.name}>{p.name}</option>)}
        </select>
      ),
      text: t => t.preserv,
    },
    {
      field: 'storage', header: 'StorageID', width: 130, sortable: false,
      render: t => (
        <select value={t.storage} aria-label={`${t.name} storage`} style={CELL_INPUT}
          onChange={e => setTestField(sampleId, t.key, 'storage', e.target.value)}>
          <option value="">N/A</option>
          {bl.storages.map(s => <option key={s.oid} value={s.name}>{s.name}</option>)}
        </select>
      ),
      text: t => t.storage,
    },
    {
      field: 'cond', header: 'StorageCondition', width: 170, sortable: false,
      text: t => {
        const firstLetter = (t.bottle || '').split(',')[0]?.trim() || 'A';
        return t.bd?.[firstLetter]?.storageCondition ?? '';
      },
      render: t => {
        const firstLetter = (t.bottle || '').split(',')[0]?.trim() || 'A';
        return (
          <ComboSelect value={t.bd?.[firstLetter]?.storageCondition ?? ''} placeholder="N/A"
            options={bl.storageConditions.map(sc => sc.name)}
            onChange={v => setTestField(sampleId, t.key, 'storageCondition', v)} />
        );
      },
    },
  ];

  const current = samples[active];
  // Cont-popup master-detail: the active sample's lab tests (field tests get no
  // bottle), and the one in focus (defaults to the first when nothing is clicked or
  // the clicked test is gone).
  const popupTests = (current?.tests ?? []).filter(t => !isFieldTest(t));
  const focusedTest = popupTests.find(t => t.key === focusedTestKey) ?? popupTests[0];

  return (
    <>
      <div className="sr-viewbar">
        {testMasterDetail ? (<>
          <button className={`sr-viewtab${effectiveView === 'bysample' ? ' on' : ''}`} onClick={() => setView('bysample')}>
            Containers
          </button>
          <button className={`sr-viewtab${effectiveView === 'all' ? ' on' : ''}`} onClick={() => setView('all')}>
            Container Summary
          </button>
        </>) : !hideViewToggle ? (<>
          <button className={`sr-viewtab${view === 'all' ? ' on' : ''}`} onClick={() => setView('all')}>
            All Containers
          </button>
          <button className={`sr-viewtab${view === 'bysample' ? ' on' : ''}`} onClick={() => setView('bysample')}>
            By Sample
          </button>
        </>) : null}
        <span style={{ flex: 1 }} />
        {testMasterDetail && current && (
          <label className="sr-cqty">
            <span>Container Qty:</span>
            <input type="number" min={1} max={26} value={current.v.Qty ?? '1'} aria-label="Container Qty"
              onChange={e => {
                const val = Math.max(1, Math.min(26, parseInt(e.target.value, 10) || 1));
                mutate(draft => { const d = draft[active]; if (d) { d.v.Qty = String(val); markQtyManual(d); } });
              }} />
          </label>
        )}
        <button className="ca-btn" onClick={() => void assignBottles()}><span className="tb-i">🧪</span> Assign Bottles</button>
        <button className="ca-btn" onClick={openCopyBottles}><span className="tb-i tb-i-blue">🗎</span> Copy Bottles</button>
        <button className="ca-btn" onClick={resetBottles}><span className="tb-i">↩︎</span> Reset Bottles</button>
      </div>
      {copyOpen && (
        <CopyBottlesDialog
          sampleIds={samples.map(s => s.id)} defaultFrom={samples[active]?.id ?? ''}
          onClose={() => setCopyOpen(false)} onOk={copyBottles} onWarn={onWarn} />
      )}

      {effectiveView === 'all' ? (
        <AllBottlesGrid samples={samples} setBottleField={setBottleField} bl={bl}
          containersFor={containersFor} preservativesFor={preservativesFor} />
      ) : testMasterDetail ? (
        <div className="sr-split">
          <div className="sr-split-col">
            <div className="sr-split-h">
              Tests — <span className="sr-split-id">{current?.id ?? '—'}</span>
            </div>
            <AppDataGrid<SrTest>
              rows={popupTests}
              columns={[
                { field: 'name', header: 'Test', width: 300, render: t => <span style={{ fontWeight: 600 }}>{t.name}</span> },
              ]}
              getRowId={t => t.key}
              selectedRowId={focusedTest?.key ?? null}
              onRowClick={t => setFocusedTestKey(t.key)}
              defaultPageSize={20}
              showFilters={false}
              emptyText="No tests assigned to this sample."
            />
          </div>
          <div className="sr-split-col">
            <div className="sr-split-h">
              <span style={{ flex: 1 }}>
                Bottle Assignment — <span className="sr-split-id">{focusedTest?.name ?? '—'}</span>
              </span>
            </div>
            {focusedTest && current ? (
              <BottleAssignmentGrid
                sample={current} test={focusedTest} bl={bl}
                containersFor={containersFor} preservativesFor={preservativesFor}
                pairedPreservative={(t, c) => guidePreservFor(bl.testGuide, t.testMethodOid, c)}
                onChange={(letters, bd) => updateTestBottles(current.id, focusedTest.key, letters, bd)}
              />
            ) : (
              <div className="sr-ba-empty">Select a test to view its bottles.</div>
            )}
          </div>
        </div>
      ) : (
        <div className="sr-split">
          <div className="sr-split-col">
            <div className="sr-split-h">Sample Details</div>
            <AppDataGrid<SrSample>
              rows={samples}
              columns={[
                { field: 'id', header: 'SampleID', width: 150, render: s => <span style={{ fontWeight: 600 }}>{s.id}</span> },
                {
                  field: 'qty', header: 'Qty', type: 'number', width: 60,
                  text: s => s.v.Qty ?? '1',
                  render: s => (
                    <input type="number" min={1} max={26} value={s.v.Qty ?? '1'}
                      aria-label={`${s.id} qty`} style={{ ...CELL_INPUT, width: 50, textAlign: 'center' }}
                      onChange={e => {
                        const val = Math.max(1, Math.min(26, parseInt(e.target.value, 10) || 1));
                        mutate(draft => {
                          const d = draft.find(x => x.id === s.id);
                          if (d) d.v.Qty = String(val);
                        });
                      }} />
                  ),
                },
              ]}
              getRowId={s => s.id}
              selectedRowId={current?.id ?? null}
              onRowClick={s => setActive(samples.findIndex(x => x.id === s.id))}
              defaultPageSize={20}
              showFilters={false}
              emptyText="No samples"
            />
          </div>
          <div className="sr-split-col">
            <div className="sr-split-h">
              <span style={{ flex: 1 }}>
                Test Bottles — <span className="sr-split-id">{current?.id ?? '—'}</span>
              </span>
            </div>
            <AppDataGrid<SrTest>
              rows={(current?.tests ?? []).filter(t => !isFieldTest(t))}
              columns={bottleCols(current?.id ?? '', current ?? { id: '', hold: false, v: {}, tests: [] })}
              getRowId={t => t.key}
              defaultPageSize={20}
              emptyText="No tests assigned to this sample."
            />
          </div>
        </div>
      )}

      {bottleFor && (() => {
        const s = samples.find(x => x.id === bottleFor.sampleId);
        const t = s?.tests.find(x => x.key === bottleFor.testKey);
        if (!s || !t) return null;
        return (
          <BottlePickerDialog
            sample={s} test={t} jobId={jobId} bl={bl}
            filteredContainers={containersFor(t)}
            filteredPreservatives={preservativesFor(t)}
            guideContainer={guideFirstDefault(t.testMethodOid ? tgContainers.get(t.testMethodOid) : undefined)}
            guidePreserv={guideFirstDefault(t.testMethodOid ? tgPreservatives.get(t.testMethodOid) : undefined)}
            onClose={() => setBottleFor(null)}
            onApply={(bottles, bd) => {
              mutate(draft => {
                const ds = draft.find(x => x.id === bottleFor.sampleId);
                const dt = ds?.tests.find(x => x.key === bottleFor.testKey);
                if (!dt) return;
                dt.bottle = bottles.join(',');
                dt.bd = bd;
                const first = bottles[0];
                if (first && bd[first]) {
                  dt.container = bd[first]!.container;
                  dt.preserv   = bd[first]!.preserv;
                  dt.storage   = bd[first]!.storage;
                }
                if (ds) syncQty(ds);
              });
              setBottleFor(null);
              onSuccess(`Bottle(s) ${bottles.join(',') || '—'} assigned.`);
            }}
          />
        );
      })()}
    </>
  );
}

/**
 * Bottle Assignment grid (Containers popup, testMasterDetail right pane) — ALPACA
 * "Bottle Assignment" parity (user request 2026-10-06): one editable row per bottle
 * assigned to the focused test. The BottleID cell is a combo that opens an inline
 * bottle picker (checkbox list of the sample's bottles A…Qty); "+" adds the next
 * free bottle, the trash icon removes one (at least one is kept). Container /
 * Preservative / StorageID / StorageCondition are per-bottle dropdowns; the sample's
 * "Container Qty" (toolbar) is the bottle pool. Replaces the per-row read-only grid +
 * the separate BottlePickerDialog modal in this popup.
 */
export function BottleAssignmentGrid({ sample, test, bl, containersFor, preservativesFor, pairedPreservative, onChange }: {
  sample: SrSample;
  test: SrTest;
  bl: BottleLookups;
  /** Preservative the Test Guide pairs with a container for this test (auto-filled on pick). */
  pairedPreservative?: (t: SrTest, container: string) => string | undefined;
  containersFor: (t: SrTest) => BottleLookups['containers'];
  preservativesFor: (t: SrTest) => BottleLookups['preservatives'];
  onChange: (letters: string[], bd: Record<string, SrBottleDetail>) => void;
}) {
  const qty = Math.max(1, Number.parseInt(sample.v.Qty ?? '1', 10) || 1);
  const pool = Array.from({ length: qty }, (_, i) => String.fromCharCode(65 + i)); // A…Qty
  const letters = (() => {
    const ls = (test.bottle || '').split(',').map(s => s.trim()).filter(Boolean);
    return ls.length ? ls : ['A'];
  })();
  const [pickerFor, setPickerFor] = useState<{ letter: string; rect: DOMRect } | null>(null);

  const detailFor = (L: string): SrBottleDetail =>
    test.bd?.[L] ?? { container: test.container, preserv: test.preserv, storage: test.storage, storageCondition: '' };
  const seedNew = (): SrBottleDetail =>
    ({ container: test.container, preserv: test.preserv, storage: test.storage || '', storageCondition: '' });

  // Rebuild the whole bd map for a letter set, preserving each kept letter's detail
  // and seeding any new letter from the test's defaults.
  const bdFor = (ls: string[], added?: string): Record<string, SrBottleDetail> => {
    const bd: Record<string, SrBottleDetail> = {};
    for (const L of ls) bd[L] = L === added ? seedNew() : { ...detailFor(L) };
    return bd;
  };

  const setField = (L: string, k: keyof SrBottleDetail, v: string) => {
    const bd = bdFor(letters);
    bd[L] = { ...bd[L]!, [k]: v };
    if (k === 'container') {
      const paired = pairedPreservative?.(test, v);
      if (paired !== undefined) bd[L]!.preserv = paired;
    }
    onChange(letters, bd);
  };

  const addBottle = () => {
    const free = pool.find(a => !letters.includes(a));
    if (!free) return;                               // pool exhausted — raise Container Qty
    const next = [...letters, free].sort();
    onChange(next, bdFor(next, free));
  };

  const removeBottle = (L: string) => {
    if (letters.length <= 1) return;                 // keep ≥ 1 bottle (ALPACA parity)
    const next = letters.filter(x => x !== L);
    onChange(next, bdFor(next));
  };

  // Bottle-picker OK: the checked set becomes the test's bottles (kept-order by pool).
  const applyPicker = (checked: string[]) => {
    const next = pool.filter(a => checked.includes(a));
    const final = next.length ? next : letters;      // never clear the last bottle
    const bd: Record<string, SrBottleDetail> = {};
    for (const L of final) bd[L] = letters.includes(L) ? { ...detailFor(L) } : seedNew();
    onChange(final, bd);
    setPickerFor(null);
  };

  const canAdd = letters.length < pool.length;

  return (
    <div className="sr-ba">
      <table className="sr-ba-table">
        <thead>
          <tr>
            <th style={{ width: '16%' }}>BottleID</th>
            <th style={{ width: '24%' }}>Container</th>
            <th style={{ width: '24%' }}>Preservative</th>
            <th style={{ width: '13%' }}>StorageID</th>
            <th style={{ width: '15%' }}>StorageCondition</th>
            <th style={{ width: '4%' }} aria-label="Remove" />
          </tr>
        </thead>
        <tbody>
          {letters.map(L => {
            const d = detailFor(L);
            return (
              <tr key={L}>
                <td>
                  <button type="button" className="sr-ba-combo" aria-label={`Bottle ${L}`}
                    onClick={e => setPickerFor({ letter: L, rect: e.currentTarget.getBoundingClientRect() })}>
                    <span>{L}</span><i className="ti ti-chevron-down" />
                  </button>
                </td>
                <td>
                  <select value={d.container} aria-label={`Bottle ${L} container`} style={CELL_INPUT}
                    onChange={e => setField(L, 'container', e.target.value)}>
                    <option value="">N/A</option>
                    {containersFor(test).map(c => <option key={c.oid} value={c.name}>{c.name}</option>)}
                  </select>
                </td>
                <td>
                  <select value={d.preserv} aria-label={`Bottle ${L} preservative`} style={CELL_INPUT}
                    onChange={e => setField(L, 'preserv', e.target.value)}>
                    <option value="">N/A</option>
                    {preservativesFor(test).map(p => <option key={p.oid} value={p.name}>{p.name}</option>)}
                  </select>
                </td>
                <td>
                  <select value={d.storage} aria-label={`Bottle ${L} storage`} style={CELL_INPUT}
                    onChange={e => setField(L, 'storage', e.target.value)}>
                    <option value="">N/A</option>
                    {bl.storages.map(s => <option key={s.oid} value={s.name}>{s.name}</option>)}
                  </select>
                </td>
                <td>
                  <ComboSelect value={d.storageCondition} placeholder="N/A"
                    options={bl.storageConditions.map(sc => sc.name)}
                    onChange={v => setField(L, 'storageCondition', v)} />
                </td>
                <td className="sr-ba-del">
                  <button type="button" aria-label={`Remove bottle ${L}`} title="Remove bottle"
                    disabled={letters.length <= 1} onClick={() => removeBottle(L)}>
                    <i className="ti ti-trash" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button type="button" className="sr-ba-add" onClick={addBottle} disabled={!canAdd}
        aria-label="Add bottle"
        title={canAdd ? 'Add a bottle' : 'Increase Container Qty to add more bottles'}>
        <i className="ti ti-plus" />
      </button>

      {pickerFor && (
        <BottlePickerPopover
          anchor={pickerFor.rect} pool={pool} selected={letters}
          onCancel={() => setPickerFor(null)} onApply={applyPicker}
        />
      )}
    </div>
  );
}

/** Inline bottle picker for the Bottle Assignment BottleID combo (ALPACA parity,
 *  user request 2026-10-06): a floating panel listing the sample's bottles
 *  (Sample ID / Bottle ID) with checkboxes + a search box; OK applies the checked
 *  set. Clamped within the viewport; a scrim closes it on an outside click. */
function BottlePickerPopover({ anchor, pool, selected, onCancel, onApply }: {
  anchor: DOMRect; pool: string[]; selected: string[];
  onCancel: () => void; onApply: (checked: string[]) => void;
}) {
  const [checked, setChecked] = useState<Set<string>>(() => new Set(selected));
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  // Bottle IDs only (A, B, C…) — the dialog header already names the sample.
  const shown = pool.filter(L => !needle || L.toLowerCase().includes(needle));
  const toggle = (L: string) => setChecked(s => {
    const n = new Set(s); if (n.has(L)) n.delete(L); else n.add(L); return n;
  });
  const allOn = shown.length > 0 && shown.every(L => checked.has(L));
  const toggleAll = () => setChecked(s => {
    const n = new Set(s);
    if (allOn) shown.forEach(L => n.delete(L)); else shown.forEach(L => n.add(L));
    return n;
  });
  const width = 300;
  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));
  const top = Math.min(anchor.bottom + 2, window.innerHeight - 248);
  return (
    <>
      <div className="sr-ba-scrim" onClick={onCancel} />
      <div className="sr-ba-pop" style={{ left, top, width }} role="dialog" aria-label="Select bottles">
        <div className="sr-ba-pop-s">
          <i className="ti ti-search" />
          <input autoFocus placeholder="Search bottle…" value={q}
            onChange={e => setQ(e.target.value)} aria-label="Search bottle" />
        </div>
        <div className="sr-ba-pop-list">
          <table className="sr-ba-pop-table">
            <thead>
              <tr>
                <th className="sr-ba-pop-cb">
                  <input type="checkbox" aria-label="Select all bottles" checked={allOn} onChange={toggleAll} />
                </th>
                <th>Bottle ID</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(L => (
                <tr key={L} className={checked.has(L) ? 'on' : ''} onClick={() => toggle(L)}>
                  <td className="sr-ba-pop-cb">
                    <input type="checkbox" checked={checked.has(L)} aria-label={`Bottle ${L}`}
                      onChange={() => toggle(L)} onClick={e => e.stopPropagation()} />
                  </td>
                  <td>{L}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={3} className="sr-ba-pop-empty">No bottles.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="sr-ba-pop-foot">
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => onApply([...checked])}>OK</button>
          <button type="button" className="ca-btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/**
 * Containers popup (ALPACA "Containers" / "Cont" row action) — a two-grid window
 * scoped to the CLICKED sample (user request 2026-10-05): left pane lists that
 * sample's test NAMES, right pane shows ONLY the focused test's bottle row
 * (BottleID / Method / Container / Preservative / Storage / StorageCondition), with
 * Assign / Copy / Reset Bottles. Edits are buffered so "Save changes" commits them to
 * the form and "Cancel changes" discards them (DevExpress batch-edit parity). The
 * actual DB write happens on the page-level Save (CLAUDE.md §1).
 */
function ContainersPopup({ samples, clickedId, setSamples, onClose, onSuccess, onWarn, jobId, primaryMatrixOid, initialTestKey }: {
  initialTestKey?: string | null;
  samples: SrSample[];
  clickedId: string;
  setSamples: (next: SrSample[]) => void;
  onClose: () => void;
  onSuccess: (m: string) => void;
  onWarn: (m: string) => void;
  jobId: string;
  primaryMatrixOid?: string | null;
}) {
  // Working buffer (deep copy) so Save / Cancel changes are real commit / discard.
  const [buffer, setBuffer] = useState<SrSample[]>(
    () => samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) })));
  const [activeIdx, setActiveIdx] = useState(
    () => Math.max(0, samples.findIndex(s => s.id === clickedId)));

  // "saved" here means staged into the parent's state — the DB write is the outer Save,
  // so the guard must know there is unsaved work.
  const commit = () => { setSamples(buffer); markSamplesEdited(); onSuccess('Container changes saved.'); onClose(); };

  return (
    <AppDialog open width={1200} icon="ti-bottle" submitOnEnter={false}
      title={`Containers — ${clickedId}`} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel changes</button>
        <button className="ca-btn ca-btn-solid" onClick={commit}>Save changes</button>
      </>}>
      <ContainersTab
        samples={buffer} setSamples={setBuffer}
        active={activeIdx} setActive={setActiveIdx}
        onSuccess={onSuccess} onWarn={onWarn} jobId={jobId}
        primaryMatrixOid={primaryMatrixOid} hideViewToggle testMasterDetail initialTestKey={initialTestKey}
      />
    </AppDialog>
  );
}

// One row per individual bottle letter so each letter's container / preservative /
// storage is shown and editable independently (Bug 1 fix).
interface FlatBottle extends FlatTest { letter: string }

function AllBottlesGrid({ samples, setBottleField, bl, containersFor, preservativesFor }: {
  samples: SrSample[];
  setBottleField: (sampleId: string, testKey: string, letter: string, field: 'container' | 'preserv' | 'storage' | 'storageCondition', value: string) => void;
  bl: BottleLookups;
  containersFor: (t: SrTest) => BottleLookups['containers'];
  preservativesFor: (t: SrTest) => BottleLookups['preservatives'];
}) {
  // Field-method tests are performed in the field and get NO lab bottle/container, so
  // they are excluded from the Containers view (ALPACA parity — the bottle-allocation
  // grid skips IsFieldTest tests; user report 2026-09-29).
  // Expand each test into one row per bottle letter so container/preservative/storage
  // are shown per letter rather than collapsed to the test-level first-letter value.
  const rows: FlatBottle[] = useMemo(
    () => samples.flatMap(s =>
      s.tests
        .filter(t => !isFieldTest(t))
        .flatMap(t =>
          (t.bottle || 'A').split(',').filter(Boolean).map(b => ({
            key: `${s.id}|${t.key}|${b.trim()}`,
            sample: s, test: t, letter: b.trim(),
          })),
        ),
    ),
    [samples],
  );
  const cols: AppColumn<FlatBottle>[] = [
    { field: 'sampleId', header: 'SampleID', width: 150, text: r => r.sample.id,
      render: r => <span style={{ fontWeight: 600 }}>{r.sample.id}</span> },
    { field: 'test', header: 'Test', width: 170, text: r => r.test.name },
    { field: 'bottle', header: 'BottleID', width: 90, text: r => r.letter },
    { field: 'method', header: 'Method', width: 120, text: r => r.test.method },
    {
      field: 'container', header: 'Containers', width: 170, sortable: false,
      text: r => r.test.bd?.[r.letter]?.container ?? r.test.container,
      render: r => (
        <select
          value={r.test.bd?.[r.letter]?.container ?? r.test.container}
          aria-label={`${r.sample.id} ${r.test.name} ${r.letter} container`}
          style={CELL_INPUT}
          onChange={e => setBottleField(r.sample.id, r.test.key, r.letter, 'container', e.target.value)}>
          <option value="">N/A</option>
          {containersFor(r.test).map(c => <option key={c.oid} value={c.name}>{c.name}</option>)}
        </select>
      ),
    },
    {
      field: 'preserv', header: 'Preservative', width: 140, sortable: false,
      text: r => r.test.bd?.[r.letter]?.preserv ?? r.test.preserv,
      render: r => (
        <select
          value={r.test.bd?.[r.letter]?.preserv ?? r.test.preserv}
          aria-label={`${r.sample.id} ${r.test.name} ${r.letter} preservative`}
          style={CELL_INPUT}
          onChange={e => setBottleField(r.sample.id, r.test.key, r.letter, 'preserv', e.target.value)}>
          <option value="">N/A</option>
          {preservativesFor(r.test).map(p => <option key={p.oid} value={p.name}>{p.name}</option>)}
        </select>
      ),
    },
    {
      field: 'storage', header: 'StorageID', width: 130, sortable: false,
      text: r => r.test.bd?.[r.letter]?.storage ?? r.test.storage,
      render: r => (
        <select
          value={r.test.bd?.[r.letter]?.storage ?? r.test.storage}
          aria-label={`${r.sample.id} ${r.test.name} ${r.letter} storage`}
          style={CELL_INPUT}
          onChange={e => setBottleField(r.sample.id, r.test.key, r.letter, 'storage', e.target.value)}>
          <option value="">N/A</option>
          {bl.storages.map(s => <option key={s.oid} value={s.name}>{s.name}</option>)}
        </select>
      ),
    },
    {
      field: 'cond', header: 'StorageCondition', width: 170, sortable: false,
      text: r => r.test.bd?.[r.letter]?.storageCondition ?? '',
      render: r => (
        <ComboSelect
          value={r.test.bd?.[r.letter]?.storageCondition ?? ''} placeholder="N/A"
          options={bl.storageConditions.map(sc => sc.name)}
          onChange={v => setBottleField(r.sample.id, r.test.key, r.letter, 'storageCondition', v)} />
      ),
    },
  ];
  return (
    <>
      <div className="sr-split-h" style={{ borderRadius: '4px 4px 0 0' }}>
        All Test Bottles — {rows.length} row(s) across {samples.length} sample(s)
      </div>
      <AppDataGrid<FlatBottle>
        rows={rows} columns={cols} getRowId={r => r.key}
        defaultPageSize={100} emptyText="No tests assigned yet."
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Parameters tab — one row per sample × test × included analyte
// ─────────────────────────────────────────────────────────────────────────────
interface FlatParam { key: string; sample: SrSample; test: SrTest; pm: string }

/** Like {@link ContainersTabProps} - the Parameters tab reads only these seven,
 *  so Sample Transfer can render the very same component (standing rule 8).
 *  matrixMap / collectorMap / unitOptions are optional; without them the Matrix
 *  and AnalyzedBy cells fall back to the raw stored value and the Units dropdown
 *  is empty, so a host that cannot load the catalogs still renders. */
export type ParametersTabProps = Pick<SampleLoginViewProps,
  'samples' | 'setSamples' | 'onSuccess' | 'confirmAction' | 'matrixMap' | 'unitOptions' | 'employeeOptions' | 'collectorMap'>;

export function ParametersTab({ samples, setSamples, onSuccess, confirmAction, matrixMap, unitOptions, employeeOptions, collectorMap, readOnly }: ParametersTabProps & { readOnly?: boolean }) {
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  const [pVer, setPVer] = useState(_paramVer);

  useEffect(() => {
    let cancel = false;
    const unique = new Map<string, { name: string; method: string; comp: string }>();
    for (const s of samples) for (const t of s.tests) {
      const k = paramCacheKey(t);
      if (!_paramCache.has(k)) unique.set(k, t);
    }
    if (unique.size === 0) return;
    Promise.all([...unique.values()].map(fetchTestParams)).then(() => {
      if (!cancel) setPVer(_paramVer);
    });
    return () => { cancel = true; };
  }, [samples]);

  const rows: FlatParam[] = useMemo(
    () => samples.flatMap(s => s.tests.flatMap(t =>
      (cachedParams(t) ?? [t.name])
        .filter(pm => !t.params || t.params[pm] !== false)
        .map(pm => ({ key: `${s.id}|${t.key}|${pm}`, sample: s, test: t, pm })))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [samples, pVer],
  );

  const mutate = (fn: (draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    fn(draft);
    setSamples(draft);
    markSamplesEdited();
  };

  const setResult = (r: FlatParam, field: keyof SrParamData, value: string) => {
    mutate(draft => {
      const t = draft.find(s => s.id === r.sample.id)?.tests.find(x => x.key === r.test.key);
      if (!t) return;
      t.paramData[r.pm] = {
        result: '', units: '', analysisDate: '', analyzedBy: '',
        ...(t.paramData[r.pm] ?? {}), [field]: value,
      };
    });
  };

  /** For field tests, AnalysisDate DEFAULTS to collection date+time and
   *  AnalyzedBy DEFAULTS to the collector — but both are now editable (ALPACA
   *  SampleParameter_ListView batch edit): the user can override either, and the
   *  stored override wins over this default (#9, 2026-10-09). */
  const fieldAutoValue = (r: FlatParam, field: keyof SrParamData): string => {
    if (!isFieldTest(r.test)) return '';
    if (field === 'analysisDate') {
      const d = r.sample.v.DateCollected ?? '';
      const t = r.sample.v.TimeCollected ?? '';
      return d && t ? `${d}T${t}` : d ? `${d}T00:00` : '';
    }
    if (field === 'analyzedBy') {
      // Show the collector's NAME, not the raw Collector Oid/GUID (user report
      // 2026-09-23). The map keys oids lowercase (.NET Guid) AND name→name; fall
      // back to the raw value only if nothing matches.
      const cid = r.sample.v.Collector ?? '';
      if (!cid) return '';
      return collectorMap?.[cid] ?? collectorMap?.[cid.toLowerCase()] ?? cid;
    }
    return '';
  };

  const resultCell = (r: FlatParam, field: keyof SrParamData, type = 'text'): React.ReactNode => {
    const field_ = isFieldTest(r.test);
    // All four result columns (Result · Units · AnalysisDate · AnalyzedBy) are
    // user-editable for field tests; lab-method params stay read-only (entered
    // later in Result Entry). AnalysisDate/AnalyzedBy auto-populate from the
    // collection date / collector as a DEFAULT, but a stored override wins so the
    // user can change them (#9, 2026-10-09).
    const editable = !readOnly && field_;
    const autoVal = (field === 'analysisDate' || field === 'analyzedBy') ? fieldAutoValue(r, field) : '';
    const val = (r.test.paramData[r.pm]?.[field] ?? '') || autoVal;
    if (field === 'analyzedBy' && employeeOptions?.length) {
      // Employee roster dropdown (ALPACA AnalyzedBy lookup). Prepend the current
      // value when it is not in the roster so a legacy/collector name still shows.
      const opts = employeeOptions.includes(val) || !val ? employeeOptions : [val, ...employeeOptions];
      return (
        <select
          value={val}
          disabled={!editable}
          title={!field_ ? 'Lab method — results are entered in Result Entry' : undefined}
          aria-label={`${r.sample.id} ${r.pm} analyzed by`}
          style={CELL_INPUT}
          size={1}
          onChange={e => setResult(r, field, e.target.value)}
          className="sr-unit-sel"
        >
          <option value="">N/A</option>
          {opts.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      );
    }
    if (field === 'units' && unitOptions?.length) {
      return (
        <select
          value={val}
          disabled={!editable}
          title={!field_ ? 'Lab method — results are entered in Result Entry' : undefined}
          aria-label={`${r.sample.id} ${r.pm} units`}
          style={CELL_INPUT}
          size={1}
          onChange={e => setResult(r, field, e.target.value)}
          className="sr-unit-sel"
        >
          <option value="">N/A</option>
          {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      );
    }
    return (
      <input
        type={type}
        step={type === 'datetime-local' ? 60 : undefined}
        value={val}
        disabled={!editable}
        title={!field_ ? 'Lab method — results are entered in Result Entry' : undefined}
        aria-label={`${r.sample.id} ${r.pm} ${field}`}
        style={CELL_INPUT}
        className={type === 'datetime-local' && !val ? 'sr-dt-empty' : undefined}
        onChange={e => setResult(r, field, e.target.value)}
      />
    );
  };

  const cols: AppColumn<FlatParam>[] = [
    { field: 'sampleId', header: 'SampleID', width: 150, text: r => r.sample.id,
      render: r => <span style={{ fontWeight: 600 }}>{r.sample.id}</span> },
    { field: 'bottle', header: 'BottleID', width: 90, text: r => r.test.bottle || '—' },
    { field: 'test', header: 'Test', width: 150, text: r => r.test.name },
    { field: 'method', header: 'Method', width: 120, text: r => r.test.method },
    { field: 'matrix', header: 'Matrix', width: 120, text: r => matrixMap?.[r.sample.v.SampleMatrix ?? ''] ?? '' },
    { field: 'pm', header: 'Parameter', width: 170 },
    { field: 'result', header: 'Result', width: 110, sortable: false,
      text: r => r.test.paramData[r.pm]?.result ?? '', render: r => resultCell(r, 'result') },
    { field: 'units', header: 'Units', width: 120, sortable: false,
      text: r => r.test.paramData[r.pm]?.units ?? '', render: r => resultCell(r, 'units') },
    { field: 'analysisDate', header: 'AnalysisDate', width: 190, sortable: false,
      text: r => (r.test.paramData[r.pm]?.analysisDate ?? '') || fieldAutoValue(r, 'analysisDate'),
      render: r => resultCell(r, 'analysisDate', 'datetime-local') },
    { field: 'analyzedBy', header: 'AnalyzedBy', width: 140, sortable: false,
      text: r => (r.test.paramData[r.pm]?.analyzedBy ?? '') || fieldAutoValue(r, 'analyzedBy'),
      render: r => resultCell(r, 'analyzedBy') },
  ];

  return (
    <>
      <div className="sr-subtoolbar">
        <span className="sr-sublbl">
          Parameters — every analyte for this job (field-method results editable here)
        </span>
        {!readOnly && (
        <button className="ca-btn" disabled={sel.size === 0}
          onClick={() => confirmAction('Remove parameters', `Remove ${sel.size} analyte(s) from their tests?`, () => {
            mutate(draft => {
              for (const key of sel) {
                const [sampleId, testKey, pm] = String(key).split('|');
                const t = draft.find(s => s.id === sampleId)?.tests.find(x => x.key === testKey);
                if (!t || !pm) continue;
                t.params ??= Object.fromEntries((cachedParams(t) ?? [t.name]).map(p => [p, true]));
                t.params[pm] = false;
                delete t.paramData[pm];
              }
              for (const s of draft) {
                for (const t of s.tests) {
                  if (!t.params) continue;
                  const on = Object.values(t.params).filter(Boolean).length;
                  if (on < 1) {
                    const first = Object.keys(t.params)[0];
                    if (first) t.params[first] = true;
                  }
                }
              }
            });
            setSel(new Set());
            onSuccess('Parameter(s) removed.');
          }, true)}>
          <span className="tb-i tb-i-red">🗑</span> Delete
        </button>
        )}
        <ExportMenu filename="parameters" triggerClassName="ca-btn"
          headers={['SampleID', 'BottleID', 'Test', 'Method', 'Matrix', 'Parameter', 'Result', 'Units', 'AnalysisDate', 'AnalyzedBy']}
          rows={rows.map(r => [r.sample.id, r.test.bottle, r.test.name, r.test.method,
            matrixMap?.[r.sample.v.SampleMatrix ?? ''] ?? '', r.pm,
            r.test.paramData[r.pm]?.result ?? '', r.test.paramData[r.pm]?.units ?? '',
            (r.test.paramData[r.pm]?.analysisDate ?? '') || fieldAutoValue(r, 'analysisDate'),
            (r.test.paramData[r.pm]?.analyzedBy ?? '') || fieldAutoValue(r, 'analyzedBy')])} />
      </div>
      <AppDataGrid<FlatParam>
        rows={rows} columns={cols} getRowId={r => r.key}
        selectable={!readOnly} selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={100}
        emptyText="No parameters — assign tests (and pick parameters) first."
      />
    </>
  );
}

/**
 * "Test Edit" popup (ALPACA TestEdit_DashboardView) — a Sample ID dropdown over a
 * light Add Test / Remove Test / Copy Tests toolbar and a Matrix · Test · Method ·
 * GroupTest grid. Unlike TestAssignmentDialog it has NO Save: like legacy, each
 * action edits the sample's tests in the form straight away (the page-level Save
 * persists them), so the grid below stays in step.
 */
export function TestEditDialog({ samples, initialId, matrixName, testRows, groupChildren, jobOid, onTestsChange, onCopyTests, onClose, onWarn }: {
  /** Saved job's Oid — lets Remove ask the server which tests are protected. */
  jobOid?: string | null;
  samples: SrSample[];
  initialId: string;
  matrixName: (s: SrSample) => string;
  testRows: string[][];
  groupChildren: Record<string, { name: string; method: string; comp: string; isFieldTest?: boolean; oid?: string }[]>;
  onTestsChange: (sampleId: string, tests: SrTest[]) => void;
  onCopyTests: (sampleId: string) => void;
  onClose: () => void;
  onWarn: (m: string) => void;
}) {
  const [sampleId, setSampleId] = useState(initialId);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [pick, setPick] = useState<Set<string | number>>(new Set());
  const [pickFilter, setPickFilter] = useState('');
  const sample = samples.find(s => s.id === sampleId) ?? samples[0];
  const tests = sample?.tests ?? [];
  const matrix = sample ? matrixName(sample) : '';

  type Row = { key: string; matrix: string; test: string; method: string; group: string };
  const rows = useMemo<Row[]>(() => tests.map(t => ({
    key: t.key, matrix, test: t.name, method: t.method, group: t.testGroupName || '',
  })), [tests, matrix]);
  const cols: AppColumn<Row>[] = [
    { field: 'matrix', header: 'Matrix', width: 120 },
    { field: 'test', header: 'Test', width: 190 },
    { field: 'method', header: 'Method', width: 150 },
    { field: 'group', header: 'GroupTest', width: 150 },
  ];

  type Avail = { id: number; test: string; method: string; component: string; isGroup: boolean };
  const have = useMemo(() => new Set(tests.map(t => `${t.name}|${t.comp || 'Default'}`)), [tests]);
  const avail = useMemo<Avail[]>(() => {
    const q = pickFilter.trim().toLowerCase();
    return testRows
      .map((r, i): Avail => ({ id: i, test: r[0] ?? '', method: r[1] ?? '', isGroup: r[2] === '1', component: r[3] ?? '' }))
      .filter(a => a.test && !have.has(`${a.test}|${a.component || 'Default'}`))
      .filter(a => !q || `${a.test} ${a.method} ${a.component}`.toLowerCase().includes(q));
  }, [testRows, have, pickFilter]);
  const availCols: AppColumn<Avail>[] = [
    { field: 'test', header: 'Test', width: 190 },
    { field: 'method', header: 'Method', width: 130 },
    { field: 'component', header: 'Component', width: 110 },
  ];

  const addPicked = () => {
    if (!sample) return;
    if (pick.size === 0) { onWarn('Tick at least one test to add.'); return; }
    const next = tests.map(copyTest);
    const exists = (name: string, comp: string) => next.some(t => t.name === name && t.comp === (comp || 'Default'));
    const toCache: { name: string; method: string; comp: string }[] = [];
    const entered = sample.v.EnteredDate ?? '';
    for (const id of pick) {
      const r = testRows[Number(id)];
      if (!r) continue;
      const name = r[0] ?? '', method = r[1] ?? '', component = r[3] ?? '';
      if (!name) continue;
      if (r[2] === '1') {
        for (const ch of defaultComponentChildren(groupChildren[name] ?? [])) {
          if (exists(ch.name, ch.comp || 'Default')) continue;
          next.push(mkTest(ch.name, ch.method, ch.comp || undefined, entered, name, ch.isFieldTest, ch.oid || undefined));
          toCache.push({ name: ch.name, method: ch.method, comp: ch.comp || 'Default' });
        }
      } else {
        const comp = component || 'Default';
        if (exists(name, comp)) continue;
        next.push(mkTest(name, method, comp, entered, '', r[4] === '1', r[5] || undefined));
        toCache.push({ name, method, comp });
      }
    }
    onTestsChange(sample.id, next);
    for (const c of toCache) void fetchTestParams(c);
    setPick(new Set()); setPickFilter(''); setAddOpen(false);
  };
  const removeChecked = async () => {
    if (!sample) return;
    if (sel.size === 0) { onWarn('Tick at least one test to remove.'); return; }
    // GoLive delete guards — a saved test that is in use stays (see testDeleteBlockMessage).
    const blocked = await testDeleteBlockMessage(jobOid,
      tests.filter(t => sel.has(t.key)).map(t => ({ sampleId: sample.id, test: t })));
    if (blocked) { onWarn(blocked); return; }
    // Bug 15: removing a member of a Group Test leaves the group incomplete, so the
    // remaining members drop the Test Group Name (same rule as the Test Assignment
    // popup's Remove).
    const affectedGroups = new Set(
      tests.filter(t => sel.has(t.key) && t.testGroupName).map(t => t.testGroupName));
    const remaining = tests.filter(t => !sel.has(t.key)).map(copyTest);
    for (const g of affectedGroups) {
      const members = defaultComponentChildren(groupChildren[g] ?? []);
      const present = new Set(remaining.map(t => `${t.name}|${t.comp || 'Default'}`));
      const complete = members.length > 0
        && members.every(ch => present.has(`${ch.name}|${ch.comp || 'Default'}`));
      if (!complete) for (const t of remaining) if (t.testGroupName === g) t.testGroupName = '';
    }
    onTestsChange(sample.id, remaining);
    setSel(new Set());
  };

  return (
    <AppDialog open title="Test Edit" icon="ti-edit" width={760} onClose={onClose} submitOnEnter={false}
      footer={<button className="ca-btn" onClick={onClose}>Close</button>}>
      <div className="sr-fgrid" style={{ gridTemplateColumns: '80px minmax(0,320px)', marginBottom: 10, alignItems: 'center' }}>
        <label style={{ fontWeight: 600 }}>Sample ID:</label>
        <select value={sample?.id ?? ''} onChange={e => { setSampleId(e.target.value); setSel(new Set()); }}>
          {samples.map(s => <option key={s.id} value={s.id}>{s.id}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button className="ca-btn" onClick={() => setAddOpen(true)}><span className="tb-i tb-i-green">＋</span> Add Test</button>
        <button className="ca-btn" disabled={sel.size === 0} onClick={removeChecked}><span className="tb-i tb-i-grey">−</span> Remove Test</button>
        <button className="ca-btn" onClick={() => sample && onCopyTests(sample.id)}>Copy Tests</button>
      </div>
      <div style={{ minHeight: 220 }}>
        <AppDataGrid<Row> rows={rows} columns={cols} getRowId={r => r.key}
          selectable selectedIds={sel} onSelectionChange={setSel}
          fixedLayout defaultPageSize={20} emptyText="No data to display" />
      </div>

      {addOpen && (
        <AppDialog open title="Add Test" icon="ti-plus" width={640} onClose={() => { setAddOpen(false); setPick(new Set()); }}
          footer={<>
            <button className="ca-btn" onClick={() => { setAddOpen(false); setPick(new Set()); }}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={addPicked}>OK</button>
          </>}>
          <div className="sr-pick-f" style={{ border: '1px solid var(--ai-line)', borderRadius: 5, marginBottom: 6 }}>
            <input placeholder="Enter text to search…" value={pickFilter} onChange={e => setPickFilter(e.target.value)} />
          </div>
          <div style={{ height: 340 }}>
            <AppDataGrid<Avail> rows={avail} columns={availCols} getRowId={r => r.id}
              selectable selectedIds={pick} onSelectionChange={setPick}
              fixedLayout defaultPageSize={50} emptyText="No matches." />
          </div>
        </AppDialog>
      )}
    </AppDialog>
  );
}
